import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders, useQobuzStore } from '../lib/useQobuz';
import { isBadCoverUrl, isCoverThumbRequest, withoutCoverThumbParam, logCoverThumb404, resolveLocalAlbumCoverSync, pickCoverArtThumbHash, isCoverProxyUrl } from '../utils/coverArt';
import { placeholderDataUri } from './library/AlbumGrid';
import {
  resolveLocalAlbumCoverDisplay,
  getCoverFromCache,
  hasResolvedCoverInCache,
  isCoverIPCAvailable,
  normalizeCoverHash,
  pickAlbumFolderPath,
  subscribeCoverCacheLoaded,
} from '../lib/coverArtIPC';
import {
  getExternalDetailHash,
  isExternalCoverKnownArtless,
  requestExternalAlbumCoverHash,
  seedIndexCoverHashes,
  subscribeExternalCoverResolve,
} from '../lib/externalAlbumCoverResolve';
import {
  templateBioCollapsedTeaser,
  templateBioTeaserLongEnough,
} from './albumDetailNarrativeSections';
import {
  findIndexCoverHintsForAlbum,
  mergeLocalAlbumCoverHints,
  pickCoverHintsFromRow,
} from '../lib/localAlbumIndexCover';
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import {
  mayUseEnrichedCoverUrl,
  pickLocalLibraryDisplayCover,
  pickValidAlbumObjectCoverUrl,
  shouldDeferEnrichedCoverForLocalAlbum,
} from '../utils/localLibraryCoverPrecedence';
import { useFavorites, usePlaylists } from '../lib/useUserData';
import { getQualityTier, qualifiesHiResAudioBadge } from '../hooks/useAudioQuality';
import { formatTrackRowQualityLabel } from '../utils/audioQuality';
import HiResAudioBadge from './HiResAudioBadge';
import TrackContextMenu from './TrackContextMenu';
import AlbumMenuPlaylistPanel from './AlbumMenuPlaylistPanel';
import GenreBrowseSlideout from './GenreBrowseSlideout';
import { QueueController } from '../transport/QueueController';
import { useMaestroStore } from '../store/maestroStore';
import FormattedContent from './FormattedContent';
import useAlbumCredits from '../hooks/useAlbumCredits';
import { useUI } from '../providers/UIProvider';
import AlbumDiscoveryRows from './AlbumDiscoveryRows';
import {
  countStreamableTracks,
  findLocalAlbumMatch,
  logStreamingAlbumUnavailable,
} from '../utils/streamingAlbumCatalog';
import { isQobuzAlbumUnavailableHere } from '../utils/qobuzAlbumRightsVerdict';
import { useNavigate } from 'react-router-dom';
import AlbumTracksEmptyState from './AlbumTracksEmptyState';
import { isTrackIdShapedAlbumId, resolveQobuzAlbumIdForTrackId, albumForPlayback } from '../utils/qobuzTrackIdAlbum';
import { rekeyRecentAlbum } from '../store/recentAlbumsRekey';
import { searchQobuzAlbumIdByTitle } from '../utils/qobuzAlbumIdByTitle';
import { buildLocalLibraryAlbumUrl, encodeLocalAlbumKey } from '../utils/localAlbumNavigation';

function decodeHtmlEntities(text) {
  if (!text) return '';
  let s = String(text);
  s = s.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
  s = s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&nbsp;/g, ' ');
  s = s.replace(/&copy;?/gi, '©');
  return s;
}

/** IPC/resolver URLs only — never bind generated SVG title placeholders as resolved art. */
function resolvedLocalCoverUrl(url) {
  if (!url || isBadCoverUrl(url)) return null;
  return url;
}

/** HTTP(S) or data URLs only — never `local:album:…` stable ids as img src. */
function isRenderableCoverUrl(url) {
  if (!url || isBadCoverUrl(url)) return false;
  return !/^local:/i.test(String(url).trim());
}

function cleanBioText(text) {
  if (!text) return '';
  let s = decodeHtmlEntities(text);
  s = s.replace(/^(?:#\s+)?[^:\n]{2,80}:\s*A\s+Maestro\s+Biography\s+Overview\s*/i, '');
  s = s.replace(/\bA\s+Maestro\s+Biography\s+Overview\b\s*/gi, '');
  s = s.replace(/©[^\n]*/g, '');
  s = s.replace(/^#{1,6}\s+/gm, '');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1');
  s = s.replace(/\*([^*]+)\*/g, '$1');
  s = s.replace(/\[\[([^\]]+)\]\]/g, '$1');
  s = s.replace(/^\s*---+\s*$/gm, '');
  s = s.replace(/\r\n/g, '\n');
  const paragraphs = s.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length <= 1 && s.length > 300) {
    return s.replace(/([.!?])\s{2,}/g, '$1\x00').split('\x00').map((p) => p.trim()).filter(Boolean).join('\n\n');
  }
  return paragraphs.join('\n\n');
}

/**
 * AlbumDetailView - Premium album detail layout (better than Roon)
 * 
 * Features:
 * - Flippable album cover (front/back animation)
 * - Active genre pills → navigate to genre collection
 * - Expandable track rows with full metadata (engineer, date, sample rate, etc.)
 * - Rich bio with [[linked names]] and **bold** markdown support
 * - Metadata panel with album details
 * 
 * v2.1.0 - Fixed inline formatting for [[links]] and **bold**
 */

function formatBioText(text) {
  if (!text) return '';
  return text.split('\n').map(line => {
    const trimmed = line.trim();
    if (trimmed.length > 4 && trimmed.length < 80 && /^[A-Z]/.test(trimmed) && !/[.!?,;]$/.test(trimmed)) {
      return `<strong>${trimmed}</strong>`;
    }
    return trimmed;
  }).join('\n');
}

function cleanAlbumDescription(text) {
  if (!text) return '';
  let s = String(text);

  // Convert HTML to markdown BEFORE decoding entities so we don't lose structure
  s = s.replace(/<em>([^<]*)<\/em>/gi, '*$1*');
  s = s.replace(/<i>([^<]*)<\/i>/gi, '*$1*');
  s = s.replace(/<strong>([^<]*)<\/strong>/gi, '**$1**');
  s = s.replace(/<b>([^<]*)<\/b>/gi, '**$1**');
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
  s = s.replace(/<\/?p[^>]*>/gi, '\n\n');
  s = s.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  s = s.replace(/&#32;/g, ' ').replace(/&#91;/g, '').replace(/&#93;/g, '');
  s = s.replace(/&#(\d+);/g, (_, n) => { const c = Number(n); return (c >= 32 && c < 127) ? String.fromCharCode(c) : ' '; });
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => { const c = parseInt(h, 16); return (c >= 32 && c < 127) ? String.fromCharCode(c) : ' '; });
  s = s.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&nbsp;/g, ' ');
  s = s.replace(/&copy;?/gi, '©');
  s = s.replace(/©[^\n]*/g, '');

  // Strip citation markers — both compact [1] and spaced [ 1 ] forms
  s = s.replace(/\[\s*\d+\s*\]/g, '');
  s = s.replace(/\[\s*note\s*\d+\s*\]/gi, '');
  s = s.replace(/\[\s*citation[^\]]*\]/gi, '');
  s = s.replace(/\[\s*edit\s*\]/gi, '');
  s = s.replace(/\[\s*clarification needed\s*\]/gi, '');
  s = s.replace(/\[\s*dubious[^\]]*\]/gi, '');
  s = s.replace(/\[\s*better source needed\s*\]/gi, '');

  // Strip Wikipedia hatnotes and disambiguation notices
  s = s.replace(/"[^"]*"\s*(and\s+"[^"]*"\s*)?redirect(s)?\s+here\b[^.]*\./gi, '');
  s = s.replace(/\bfor (a list of|the|other)\b[^.]*?\bsee\b[^.]*?\./gi, '');
  s = s.replace(/\(disambiguation\)/gi, '');
  s = s.replace(/\bthis article is about\b[^.]*?\./gi, '');
  s = s.replace(/\bmay refer to:?\b[^.]*?\./gi, '');
  s = s.replace(/\bnot to be confused with\b[^.]*?\./gi, '');

  // Strip Wikipedia infobox remnants
  s = s.replace(/\bType of\s+(compilation|studio|live|soundtrack|EP|remix|greatest hits)\s+album\b/gi, '');

  // Strip footnote/reference lines
  s = s.replace(/^\^.*$/gm, '');
  s = s.replace(/\^\s*"[^"]*"[^\n]*Retrieved[^\n]*/g, '');
  s = s.replace(/\^\s*[A-Za-z].*?\.\s*Retrieved\b[^\n]*/g, '');
  s = s.replace(/Retrieved\s+\d{1,2}\s+[A-Z][a-z]+\s+\d{4}\s*\.?/g, '');
  s = s.replace(/Retrieved\s+[A-Z][a-z]+\s+\d{1,2},?\s+\d{4}\s*\.?/g, '');
  s = s.replace(/Archived from the original[^.]*?\./g, '');
  s = s.replace(/ISBN\s[\d-]+/g, '');

  s = s.replace(/^#{1,6}\s+/gm, '');
  s = s.replace(/\[\[([^\]]+)\]\]/g, '$1');
  s = s.replace(/^\s*---+\s*$/gm, '');
  s = s.replace(/[ \t]+/g, ' ');
  s = s.replace(/\r\n/g, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  s = s.trim();

  const paragraphs = s.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  if (paragraphs.length <= 1 && s.length > 300) {
    const sentences = s.replace(/([.!?])\s+/g, '$1\x00').split('\x00').filter(Boolean);
    if (sentences.length > 4) {
      const grouped = [];
      for (let i = 0; i < sentences.length; i += 3) {
        grouped.push(sentences.slice(i, i + 3).join(' '));
      }
      return grouped.join('\n\n');
    }
  }
  return paragraphs.join('\n\n');
}

function truncateToWords(text, maxWords) {
  if (!text) return { text: '', truncated: false };
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return { text, truncated: false };
  return { text: words.slice(0, maxWords).join(' '), truncated: true };
}

function isTemplateReadyAlbumText(text) {
  const v = String(text || '');
  // Album narratives are template-ready when they have real section structure.
  // Some valid album bios do not include the `---` divider, so we detect by section count.
  if (v.length < 600) return false;
  if (!/##\s*Overview\b/i.test(v)) return false;
  if (v.includes('---')) return true;
  const sectionCount = (v.match(/^##\s+/gm) || []).length;
  if (sectionCount >= 3) return true;
  return /##\s*(The Story|Track[- ]by[- ]Track|Credits|Reception|Recording|Production|Background|Legacy)\b/i.test(v);
}

function looksLikeWikipediaDump(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (/\bthis article is about\b/i.test(t)) return true;
  if (/\bmay refer to:?\b/i.test(t)) return true;
  if (/\bfor the .* see\b/i.test(t)) return true;
  if (/\bredirect(s)?\s+here\b/i.test(t)) return true;
  if (/\bnot to be confused with\b/i.test(t)) return true;
  if (/\[\s*citation needed\s*\]/i.test(t)) return true;
  if (/\bType of\s+(compilation|studio|live|soundtrack)\s+album\b/i.test(t)) return true;
  return false;
}

function extractSafeAlbumTeaser(text) {
  const raw = String(text || '').trim();
  if (!raw) return '';

  // Strip common Wikipedia hatnote sequences if present.
  let cleaned = raw
    // Remove markdown header markers so collapsed teaser reads as prose.
    .replace(/^\s*#{1,6}\s+/gm, '')
    // Remove template dividers/HR lines.
    .replace(/^\s*---+\s*$/gm, '')
    .replace(/^\s*\d{4}\s+studio album by[^\n]*\n?/i, '')
    .replace(/\bthis article is about[\s\S]*?\.\s*/i, '')
    .replace(/\bfor the .* see[^\n]*\n?/gi, '')
    .replace(/\(disambiguation\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Prefer the first ~2 sentences as a teaser.
  const sentences = cleaned.replace(/([.!?])\s+/g, '$1\x00').split('\x00').filter(Boolean);
  const teaser = sentences.slice(0, 2).join(' ').trim() || cleaned;
  if (!teaser) return '';
  return teaser.length > 520 ? `${teaser.slice(0, 517).trim()}…` : teaser;
}

// Helper to render text with [[linked names]] and **bold** markdown
// albumInfo: { title, artistName, onArtistClick } - used to replace "Overview" header
const renderNarrativeText = (text, onPersonClick, linkMap, albumInfo = null) => {
  if (!text) return null;
  
  const sanitize = (raw) => {
    const t = String(raw || '');
    return t.replace(/^\s*(\*\*)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\*\*)?\b/i, '').trimStart();
  };

  // Process inline formatting: **bold**, [[links]]
  const renderInlineFormatting = (content, keyPrefix = '') => {
    if (!content) return null;
    
    // Split by **bold** and [[links]] patterns
    const parts = content.split(/(\*\*[^*]+\*\*|\[\[[^\]]+\]\])/g);
    
    return parts.map((part, j) => {
      // Handle **bold**
      const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
      if (boldMatch) {
        return <strong key={`${keyPrefix}-${j}`} className="font-semibold text-white">{boldMatch[1]}</strong>;
      }
      
      // Handle [[links]]
      const linkMatch = part.match(/^\[\[([^\]]+)\]\]$/);
      if (linkMatch) {
        const name = linkMatch[1];
        const entry = linkMap && typeof linkMap === 'object' ? (linkMap[name] || linkMap[String(name)]) : null;
        const kind = String(entry?.kind || '').trim();
        const id = String(entry?.id || '').trim();
        const mbid = String(entry?.mbid || '').trim();
        
        // Always render as clickable link (will attempt lookup on click)
        return (
          <button
            key={`${keyPrefix}-${j}`}
            onClick={() => {
              if (!onPersonClick) return;
              if (id) return onPersonClick(id, name, { fromBio: true, strict: true, forcePerson: kind === 'person' });
              if (mbid) return onPersonClick(mbid, name, { fromBio: true, strict: true, mbid });
              // Fallback: search by name
              onPersonClick(null, name, { fromBio: true, strict: true });
            }}
            className="hover:underline transition-colors"
            style={{ color: GOLD }}
          >
            {name}
          </button>
        );
      }
      
      return <span key={`${keyPrefix}-${j}`}>{part}</span>;
    });
  };

  // Check if line is a section header (Overview, The Story, Credits, etc.)
  const isSectionHeader = (line) => {
    const trimmed = line.trim();
    const headerPatterns = [
      /^Overview\s/i,
      /^The Story\s/i,
      /^Track[- ]by[- ]Track\s/i,
      /^Credits?\s/i,
      /^Reception\s/i,
      /^Recording\s/i,
      /^Production\s/i,
      /^Background\s/i,
      /^Legacy\s/i,
    ];
    return headerPatterns.some(p => p.test(trimmed));
  };

  // Extract section header from start of paragraph
  const extractSectionHeader = (paragraph) => {
    const headerWords = ['Overview', 'The Story', 'Track by Track', 'Track-by-Track', 'Credits', 'Reception', 'Recording', 'Production', 'Background', 'Legacy'];
    for (const header of headerWords) {
      if (paragraph.toLowerCase().startsWith(header.toLowerCase())) {
        const rest = paragraph.slice(header.length).trim();
        return { header, rest };
      }
    }
    return null;
  };

  const normalizeForCompare = (s) =>
    String(s || '')
      .trim()
      .replace(/\*\*/g, '')
      .replace(/\[\[|\]\]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ');

  let sanitized = sanitize(text);
  sanitized = sanitized.replace(/&amp;/g, '&');
  sanitized = sanitized.replace(/&copy;?/gi, '©');
  sanitized = sanitized.replace(/©[^\n]*/g, '');
  // Album bios often start with "# Album Title" which duplicates the page header.
  // Strip that line so we don't show the title twice.
  if (albumInfo?.title) {
    const lines = String(sanitized || '').split('\n');
    const first = String(lines?.[0] || '').trim();
    const m = first.match(/^#\s+(.+)$/);
    if (m?.[1] && normalizeForCompare(m[1]) === normalizeForCompare(albumInfo.title)) {
      lines.shift();
      while (lines.length && !String(lines[0] || '').trim()) lines.shift();
      sanitized = lines.join('\n');
    }
  }

  return sanitized.split('\n\n').map((paragraph, i) => {
    const trimmed = paragraph.trim();
    
    // Handle # headers (H1)
    if (/^#\s+/.test(trimmed)) {
      const lines = trimmed.split('\n');
      const headerText = lines[0].replace(/^#\s+/, '').trim();
      const bodyText = lines.slice(1).join('\n').trim();
      return (
        <div key={i}>
          <h2 className="text-2xl md:text-3xl font-bold text-white mt-6 mb-2">{headerText}</h2>
          {bodyText && (
            <p className="text-white/70 mb-3 leading-relaxed" style={{ fontSize: '14px' }}>
              {renderInlineFormatting(bodyText, `p-${i}`)}
            </p>
          )}
        </div>
      );
    }

    // Handle ## headers - extract header line and process rest with inline formatting
    if (trimmed.startsWith('## ')) {
      const lines = trimmed.split('\n');
      const rawHeader = lines[0].replace('## ', '').trim();
      const headerLower = rawHeader.toLowerCase();
      let bodyLines = lines.slice(1);

      // FIX 4: Replace "Overview" with album title
      const displayHeader = (/^overview$/i.test(rawHeader) && albumInfo?.title)
        ? albumInfo.title
        : rawHeader;
      const isOverviewSection = /^overview$/i.test(rawHeader);

      const bodyText = bodyLines.join('\n').trim();
      return (
        <div key={i}>
          <h3
            className={isOverviewSection ? "font-bold mt-6 mb-2" : "text-xl font-bold text-white mt-6 mb-2"}
            style={isOverviewSection ? { fontSize: 18, fontWeight: 700, color: 'var(--text-primary, #fff)' } : undefined}
          >
            {displayHeader}
          </h3>
          {bodyText && (
            <p className="text-white/70 mb-3 leading-relaxed" style={{ fontSize: '14px' }}>
              {renderInlineFormatting(bodyText, `p-${i}`)}
            </p>
          )}
        </div>
      );
    }
    // Handle ### headers - extract header line and process rest
    if (trimmed.startsWith('### ')) {
      const lines = trimmed.split('\n');
      const headerText = lines[0].replace('### ', '').trim();
      const bodyText = lines.slice(1).join('\n').trim();
      return (
        <div key={i}>
          <h4 className="text-sm font-bold text-white/90 mt-3 mb-1">{headerText}</h4>
          {bodyText && (
            <p className="text-white/70 mb-3 leading-relaxed" style={{ fontSize: '14px' }}>
              {renderInlineFormatting(bodyText, `p-${i}`)}
            </p>
          )}
        </div>
      );
    }
    // Handle blockquotes
    if (trimmed.startsWith('> ')) {
      return (
        <blockquote key={i} className="pl-4 italic my-3" style={{ borderLeft: `2px solid ${GOLD}`, color: 'var(--text-primary, rgba(255,255,255,0.8))' }}>
          {renderInlineFormatting(trimmed.replace('> ', '').replace(/"/g, ''), `bq-${i}`)}
        </blockquote>
      );
    }
    
    // Check for inline section headers (e.g., "Overview **Abbey Road**...")
    const sectionMatch = extractSectionHeader(trimmed);
    if (sectionMatch) {
      // FIX 4: Replace "Overview" with album title
      const displayHeader = (/^overview$/i.test(sectionMatch.header) && albumInfo?.title)
        ? albumInfo.title
        : sectionMatch.header;
      const isOverview = /^overview$/i.test(sectionMatch.header);
      return (
        <div key={i}>
          <h3
            className={isOverview ? "font-bold mt-4 mb-2" : "text-base font-bold text-white mt-4 mb-2"}
            style={isOverview ? { fontSize: 18, fontWeight: 700, color: 'var(--text-primary, #fff)' } : undefined}
          >
            {displayHeader}
          </h3>
          <p className="text-white/70 mb-3 leading-relaxed" style={{ fontSize: '14px' }}>
            {renderInlineFormatting(sectionMatch.rest, `p-${i}`)}
          </p>
        </div>
      );
    }
    
    // Regular paragraph with inline formatting
    return (
      <p key={i} className="text-white/70 mb-3 leading-relaxed" style={{ fontSize: '14px' }}>
        {renderInlineFormatting(trimmed, `p-${i}`)}
      </p>
    );
  });
};

// Format duration from ms to mm:ss
const formatDuration = (ms) => {
  if (!ms) return '--:--';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format total duration
const formatTotalDuration = (ms) => {
  if (!ms) return '';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return `${hours}hr ${remainingMins}min`;
};

// Format sample rate
const formatSampleRate = (rate) => {
  if (!rate) return null;
  const num = Number(rate);
  if (num >= 1000) return `${(num / 1000).toFixed(1)}kHz`;
  return `${num}Hz`;
};

const GOLD = '#C9A84C';

const TrackRow = ({ 
  track, 
  index, 
  isPlaying, 
  isCurrentTrack = false,
  isSelected,
  expanded,
  onToggleExpand,
  onSelect, 
  onPlay,
  onPersonClick,
  onToggleFavorite,
  isFav,
  onAddToQueue,
  onShowMenu,
  recordingCredits = null,
  recordingCreditsLoading = false,
  albumSource = null,
}) => {
  const performers = useMemo(() => {
    if (!recordingCredits?.credits_by_role) return null;
    const performing = recordingCredits.credits_by_role.performing || 
                       recordingCredits.credits_by_role.vocal ||
                       recordingCredits.credits_by_role.instrument || [];
    if (!Array.isArray(performing) || performing.length === 0) return null;
    const names = performing
      .map((p) => String(p?.artist_name || p?.name || '').trim())
      .filter(Boolean);
    const seen = new Set();
    const out = [];
    for (const n of names) {
      const k = n.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(n);
      if (out.length >= 5) break;
    }
    return out;
  }, [recordingCredits]);

  const artistName = track.artistName || track.artist_name || track.artist || performers?.join(', ') || '';
  const composerName = String(track.composer || '').trim();
  const norm = (s) => String(s || '').trim().toLowerCase();
  const showComposer = composerName && norm(composerName) !== norm(artistName);
  const trackSource = track.source || albumSource || '';
  const tbd = track.maximum_bit_depth || track.maxBitDepth;
  const tsr = track.maximum_sampling_rate || track.maxSampleRate;
  const trackTier = getQualityTier(tbd, tsr);
  const trackQualityLabel = formatTrackRowQualityLabel({
    ...track,
    bit_depth: tbd,
    sample_rate: tsr,
    format: track.format,
  });

  return (
    <div style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.05))' }}>
      <div 
        className="flex items-center cursor-pointer group transition-all touch-target [-webkit-tap-highlight-color:transparent]"
        onClick={() => { onPlay(index); }}
        style={{
          minHeight: 56,
          padding: '8px 12px',
          background: isCurrentTrack ? 'rgba(201,168,76,0.08)' : index % 2 === 0 ? 'var(--bg-primary, transparent)' : 'var(--bg-secondary, rgba(0,0,0,0.02))',
          borderLeft: isCurrentTrack ? `3px solid ${GOLD}` : '3px solid transparent',
        }}
      >
        {/* Track number / play hint / VU meter */}
        <div className="flex-shrink-0 flex items-center justify-center" style={{ width: 40, gap: 4 }}>
          {isCurrentTrack ? (
            <div className={`vu-meter${!isPlaying ? ' paused' : ''}`}>
              <span /><span /><span />
            </div>
          ) : (
            <>
              <svg className="flex-shrink-0 transition-colors pointer-fine:group-hover:text-[#C9A84C]" style={{ width: 7, height: 9, color: 'var(--text-secondary, rgba(255,255,255,0.25))' }} viewBox="0 0 7 9" fill="currentColor"><path d="M0 0v9l7-4.5z"/></svg>
              <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.4))' }}>{index + 1}</span>
            </>
          )}
        </div>

        {/* Title + artist */}
        <div className="flex-1 min-w-0 px-3">
          <p className="font-semibold truncate" style={{ fontSize: 14, color: isCurrentTrack ? GOLD : 'var(--text-primary, #fff)' }}>
            {track.title}
          </p>
          {artistName && (
            <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
              {artistName}
            </p>
          )}
          {showComposer && (
            <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-tertiary, rgba(255,255,255,0.4))' }}>
              {composerName}
            </p>
          )}
        </div>

        {trackQualityLabel ? (
          qualifiesHiResAudioBadge(tbd, tsr) ? (
            <HiResAudioBadge bitDepth={tbd} sampleRate={tsr} height={16} className="flex-shrink-0 mr-2" />
          ) : (
            <span className="flex-shrink-0 text-[9px] font-extrabold px-1.5 py-0.5 rounded mr-2" style={{ background: trackTier?.bg || 'var(--bg-secondary, rgba(150,150,150,0.12))', color: trackTier?.color || '#999', border: trackTier?.border || '1px solid var(--border, rgba(0,0,0,0.08))' }}>{trackQualityLabel}</span>
          )
        ) : null}

        {/* Duration */}
        <span className="flex-shrink-0 text-sm w-14 text-right tabular-nums" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.4))' }}>{formatDuration(track.duration_ms)}</span>

        {/* Heart / favorite */}
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(); }}
          className="flex-shrink-0 p-1.5 transition ml-2"
          style={{ color: isFav ? GOLD : 'var(--text-secondary, rgba(255,255,255,0.3))' }}
          title={isFav ? 'Remove from favorites' : 'Add to favorites'}
        >
          <svg className="w-4 h-4" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>

        {/* More menu */}
        <button 
          onClick={(e) => { e.stopPropagation(); onShowMenu?.(e, track, index); }}
          className="flex-shrink-0 p-1.5 transition ml-1"
          style={{ color: 'var(--text-secondary, rgba(255,255,255,0.3))' }}
          title="More options"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
          </svg>
        </button>
      </div>
      
      {/* Expanded track details - FULL RICH METADATA */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 animate-fade-in-up">
          <div className="bg-black/30 rounded-lg p-4 border border-white/5">
            {recordingCreditsLoading ? (
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                Loading track details...
              </div>
            ) : (
              <div className="space-y-4">
                {/* Recording Location & Date */}
                {recordingCredits?.recorded_at && (
                  <div className="pb-3 border-b border-white/10">
                    <h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: GOLD }}>Recorded At</h4>
                    <p className="text-white text-sm font-medium">{recordingCredits.recorded_at.name}</p>
                    {recordingCredits.recorded_at.area && (
                      <p className="text-white/60 text-xs">{recordingCredits.recorded_at.area}</p>
                    )}
                    {recordingCredits.recorded_at.date_range && (
                      <p className="text-white/50 text-xs mt-1">{recordingCredits.recorded_at.date_range}</p>
                    )}
                  </div>
                )}
                
                {/* Songwriting Credits */}
                {(recordingCredits?.composers?.length > 0 || recordingCredits?.lyricists?.length > 0) && (
                  <div className="pb-3 border-b border-white/10">
                    <h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: GOLD }}>Songwriting</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {recordingCredits?.composers?.length > 0 && (
                        <div>
                          <p className="text-white/40 text-xs mb-1">Composer</p>
                          <div className="flex flex-wrap gap-1">
                            {recordingCredits.composers.map((c, i) => (
                              <button
                                key={i}
                                onClick={() => onPersonClick && onPersonClick(c.artist_mbid, c.artist_name, { strict: true, mbid: c.artist_mbid })}
                                className="hover:underline text-sm" style={{ color: GOLD }}
                              >
                                {c.artist_name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {recordingCredits?.lyricists?.length > 0 && (
                        <div>
                          <p className="text-white/40 text-xs mb-1">Lyricist</p>
                          <div className="flex flex-wrap gap-1">
                            {recordingCredits.lyricists.map((l, i) => (
                              <button
                                key={i}
                                onClick={() => onPersonClick && onPersonClick(l.artist_mbid, l.artist_name, { strict: true, mbid: l.artist_mbid })}
                                className="hover:underline text-sm" style={{ color: GOLD }}
                              >
                                {l.artist_name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Performance & Production Credits */}
                {recordingCredits?.credits_by_role && Object.keys(recordingCredits.credits_by_role).length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}>Credits</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(recordingCredits.credits_by_role).map(([role, entries]) => (
                        <div key={role} className="bg-black/20 rounded p-2">
                          <p className="text-white/50 text-xs capitalize mb-1.5 font-medium">{role.replace(/_/g, ' ')}</p>
                          <div className="space-y-1">
                            {(Array.isArray(entries) ? entries : []).map((e, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <button
                                  onClick={() => {
                                    const name = e?.artist_name || e?.name;
                                    if (name && onPersonClick) {
                                      onPersonClick(e?.artist_mbid || null, name, { strict: true, mbid: e?.artist_mbid });
                                    }
                                  }}
                                  className="hover:underline text-sm" style={{ color: GOLD }}
                                >
                                  {e?.artist_name || e?.name || 'Unknown'}
                                </button>
                                {e?.disambiguation && (
                                  <span className="text-white/30 text-xs">({e.disambiguation})</span>
                                )}
                                {e?.attributes && (
                                  <span className="text-white/50 text-xs italic">• {e.attributes}</span>
                                )}
                                {e?.date_range && (
                                  <span className="text-white/30 text-xs">({e.date_range})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Publishers */}
                {recordingCredits?.publishers?.length > 0 && (
                  <div className="pt-3 border-t border-white/10">
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">Publishing</h4>
                    <div className="flex flex-wrap gap-2">
                      {recordingCredits.publishers.map((p, i) => (
                        <span key={i} className="text-white/60 text-xs bg-white/5 px-2 py-1 rounded">
                          {p.label_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Format Info */}
                <div className="pt-3 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {track.format && (
                    <div>
                      <p className="text-white/40 text-xs">Format</p>
                      <p className="text-white text-sm">{track.format}</p>
                    </div>
                  )}
                  {(track.sampleRate || track.sample_rate) && (
                    <div>
                      <p className="text-white/40 text-xs">Sample Rate</p>
                      <p className="text-white text-sm">{formatSampleRate(track.sampleRate || track.sample_rate)}</p>
                    </div>
                  )}
                  {(track.bitDepth || track.bit_depth) && (
                    <div>
                      <p className="text-white/40 text-xs">Bit Depth</p>
                      <p className="text-white text-sm">{track.bitDepth || track.bit_depth}-bit</p>
                    </div>
                  )}
                  {track.discNumber && track.discNumber > 1 && (
                    <div>
                      <p className="text-white/40 text-xs">Disc</p>
                      <p className="text-white text-sm">{track.discNumber}</p>
                    </div>
                  )}
                </div>
                
                {/* Fallback for basic credits when /full endpoint not available */}
                {!recordingCredits?.credits_by_role && !recordingCredits?.recorded_at && (
                  <p className="text-white/40 text-sm italic">No detailed credits available for this track.</p>
                )}
              </div>
            )}
            
            {/* Track story if available */}
            {track.story && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <h4 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">About This Track</h4>
                <p className="text-white/70 text-sm leading-relaxed">{track.story}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Flippable Album Cover Component
const FlippableAlbumCover = ({ frontUrl, backUrl, title, size = 400, onOpenFullView, fallbackUrl = null, wallCoverUrl = null, onProxyRecover = null }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [imgError, setImgError] = useState(false);
  // When the primary `frontUrl` 404s, fall through thumb → IPC recover → wall tile → online → title placeholder.
  const [activeUrl, setActiveUrl] = useState(frontUrl);
  const [triedFallback, setTriedFallback] = useState(false);
  const [triedWall, setTriedWall] = useState(false);
  const [triedIpcRecover, setTriedIpcRecover] = useState(false);
  const placeholderUrl = placeholderDataUri(title || 'Untitled');
  useEffect(() => {
    setActiveUrl(frontUrl);
    setTriedFallback(false);
    setTriedWall(false);
    setTriedIpcRecover(false);
    setImgError(false);
  }, [frontUrl, fallbackUrl, wallCoverUrl, onProxyRecover]);
  const handleImgError = () => {
    if (isCoverThumbRequest(activeUrl)) {
      const fullUrl = withoutCoverThumbParam(activeUrl);
      if (fullUrl && fullUrl !== activeUrl) {
        logCoverThumb404(activeUrl);
        setActiveUrl(fullUrl);
        return;
      }
    }
    if (
      !triedIpcRecover
      && onProxyRecover
      && isCoverProxyUrl(activeUrl)
    ) {
      setTriedIpcRecover(true);
      Promise.resolve(onProxyRecover()).then((url) => {
        if (url) {
          setActiveUrl(url);
          setImgError(false);
        } else if (!triedWall && wallCoverUrl && wallCoverUrl !== activeUrl) {
          setTriedWall(true);
          setActiveUrl(wallCoverUrl);
        } else if (!triedFallback && fallbackUrl && fallbackUrl !== activeUrl) {
          setTriedFallback(true);
          setActiveUrl(fallbackUrl);
        } else {
          setImgError(true);
        }
      });
      return;
    }
    if (!triedWall && wallCoverUrl && wallCoverUrl !== activeUrl) {
      setTriedWall(true);
      setActiveUrl(wallCoverUrl);
      return;
    }
    if (!triedFallback && fallbackUrl && fallbackUrl !== activeUrl) {
      setTriedFallback(true);
      setActiveUrl(fallbackUrl);
    } else {
      setImgError(true);
    }
  };
  const hasBack = !!backUrl;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const viewportCap = typeof window !== 'undefined' ? window.innerWidth - 48 : size;
  const resolvedSize = isMobile ? Math.min(Math.max(200, size), viewportCap) : Math.max(280, size);

  return (
    <div 
      className="relative cursor-pointer group"
      style={{ 
        width: resolvedSize, 
        height: resolvedSize,
        perspective: '1000px'
      }}
      onClick={() => onOpenFullView ? onOpenFullView() : (hasBack && setIsFlipped(!isFlipped))}
    >
      <div 
        className="relative w-full h-full transition-transform duration-700"
        style={{ 
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        <div 
          className="absolute w-full h-full overflow-hidden"
          style={{ backfaceVisibility: 'hidden', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
        >
          {activeUrl && !imgError ? (
            <img 
              src={activeUrl} 
              alt={title}
              className="w-full h-full object-cover"
              style={{ imageRendering: 'high-quality' }}
              onError={handleImgError}
            />
          ) : (
            <img
              src={placeholderUrl}
              alt={title}
              className="w-full h-full object-cover"
              style={{ imageRendering: 'high-quality' }}
            />
          )}
          {hasBack && (
            <div
              className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1.5 opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 transition-opacity touch-manipulation"
              onClick={(e) => { e.stopPropagation(); setIsFlipped(!isFlipped); }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          )}
        </div>
        
        {hasBack && (
          <div 
            className="absolute w-full h-full overflow-hidden"
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <img 
              src={backUrl} 
              alt={`${title} - Back Cover`}
              className="w-full h-full object-cover"
              style={{ imageRendering: 'high-quality' }}
            />
            <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1.5">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Star Rating Component
const StarRating = ({ rating = 0, max = 5 }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  return (
    <div className="flex">
      {[...Array(max)].map((_, i) => (
        <span key={i} style={{ color: i < fullStars ? GOLD : (i === fullStars && hasHalf) ? 'rgba(201,168,76,0.5)' : 'var(--text-secondary, rgba(255,255,255,0.2))' }}>
          ★
        </span>
      ))}
    </div>
  );
};

const AlbumDetailView = ({
  album,
  tracks = [],
  onTrackPlay,
  onPlayNext,
  onPlayOnlyThisTrack,
  onAddToQueue,
  onArtistClick,
  onPersonClick,
  onBioLinkClick,
  onAlbumClick,
  onGenreClick,
  isPlaying = false,
  currentTrackId = null,
  selectedTrackIndex = null,
  onTrackSelect,
  adminSection = null,
}) => {
  const { setAlbumOverlay } = useUI();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tracks');
  const [mirrorLoading, setMirrorLoading] = useState(false);
  const [mirrorError, setMirrorError] = useState(null);
  const [mirrorRelease, setMirrorRelease] = useState(null);
  const [mirrorTracks, setMirrorTracks] = useState(null);
  const [mirrorCredits, setMirrorCredits] = useState(null);
  const [apiAlbum, setApiAlbum] = useState(null);
  const [apiAlbumLoading, setApiAlbumLoading] = useState(false);
  const [qobuzFullAlbum, setQobuzFullAlbum] = useState(null);
  const [qobuzFetchedTracks, setQobuzFetchedTracks] = useState([]);
  const [qobuzCatalogLoaded, setQobuzCatalogLoaded] = useState(false);
  const [qobuzRetryNonce, setQobuzRetryNonce] = useState(0);
  const [trackIdResolvedAlbumId, setTrackIdResolvedAlbumId] = useState(null); // Oct 26 2026: page opened with a Qobuz track id
  const [recordingCreditsByMbid, setRecordingCreditsByMbid] = useState({});
  const [recordingCreditsLoading, setRecordingCreditsLoading] = useState({});
  const [expandedTrackIndex, setExpandedTrackIndex] = useState(null);
  const [backCoverUrl, setBackCoverUrl] = useState(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [menuState, setMenuState] = useState(null);
  const [genreSlideout, setGenreSlideout] = useState({ open: false, genreId: null, genreName: '' });
  const [albumToast, setAlbumToast] = useState(null);
  const albumToastTimer = useRef(null);
  const [albumMenuOpen, setAlbumMenuOpen] = useState(false);
  const [albumMenuPlaylistView, setAlbumMenuPlaylistView] = useState(false);
  const [albumMenuNewPlaylistName, setAlbumMenuNewPlaylistName] = useState('');
  const albumMenuRef = useRef(null);

  // Artist bio state (FIX 3)
  const [artistBio, setArtistBio] = useState(null);
  const [artistBioLoading, setArtistBioLoading] = useState(false);
  const [artistBioExpanded, setArtistBioExpanded] = useState(false);

  // Enrichment spinner auto-hide after 3 seconds
  const [enrichSpinnerVisible, setEnrichSpinnerVisible] = useState(true);
  const enrichTimerRef = useRef(null);

  const { isFavorite: checkFav, toggleFavorite: toggleFav } = useFavorites();
  const { playlists, createPlaylist: createPl, addTrackToPlaylist: addToPl } = usePlaylists();

  const isLocalLibraryAlbum = album?.source === 'local_library' || album?.__source === 'local_library' || album?.isLocalLibrary === true;

  const albumWithIndexCover = useMemo(() => {
    if (!isLocalLibraryAlbum) return album;
    const key = album?.album_key || album?.id || '';
    return mergeLocalAlbumCoverHints(
      album,
      pickCoverHintsFromRow(album),
      findIndexCoverHintsForAlbum(key),
    );
  }, [album, isLocalLibraryAlbum]);

  useEffect(() => {
    if (!isLocalLibraryAlbum) return;
    const key = album?.album_key || album?.id || '';
    if (!key) return;
    seedIndexCoverHashes(
      key,
      mergeLocalAlbumCoverHints(
        pickCoverHintsFromRow(album),
        findIndexCoverHintsForAlbum(key),
      ),
    );
  }, [isLocalLibraryAlbum, album?.album_key, album?.id, album?.embedded_cover_hash, album?.folder_cover_hash, album?.external_cover_hash]);

  // Album identifier for API calls - can be UUID or slug like "abbey-road-1969"
  const albumApiId = useMemo(() => {
    const raw =
      album?.musicbrainz_release_id ||
      album?.external_id ||
      album?.externalId ||
      album?.mbid ||
      album?.id ||
      null;
    const s = String(raw || '').trim();
    // Accept any non-empty ID (UUID, slug, or other format)
    return s.length > 0 ? s : null;
  }, [album]);
  
  // For cover art archive, we need a valid UUID
  const releaseGroupMbid = useMemo(() => {
    const raw =
      album?.musicbrainz_release_id ||
      album?.external_id ||
      album?.externalId ||
      album?.mbid ||
      null;
    const s = String(raw || '').trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ? s : null;
  }, [album]);

  // Auto-hide enrichment spinner after 3 seconds
  useEffect(() => {
    setEnrichSpinnerVisible(true);
    if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current);
    enrichTimerRef.current = setTimeout(() => setEnrichSpinnerVisible(false), 3000);
    return () => { if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current); };
  }, [album?.id]);

  useEffect(() => {
    if (!albumMenuOpen) return;
    const close = (e) => {
      if (albumMenuRef.current && !albumMenuRef.current.contains(e.target)) {
        setAlbumMenuOpen(false);
        setAlbumMenuPlaylistView(false);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [albumMenuOpen]);

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const directQobuzId = useMemo(() => {
    const candidates = [album?.serviceAlbumId, album?.qobuzAlbumId];
    if (album?.source === 'qobuz') candidates.push(album?.id);
    for (const c of candidates) {
      const raw = String(c || '').replace(/^qobuz:album:/, '').trim();
      if (raw && !UUID_RE.test(raw)) return raw;
    }
    return null;
  }, [album?.serviceAlbumId, album?.qobuzAlbumId, album?.id, album?.source]);

  const [resolvedQobuzId, setResolvedQobuzId] = useState(null);

  useEffect(() => {
    if (directQobuzId) { setResolvedQobuzId(null); return; }
    setResolvedQobuzId(null);
    if (isLocalLibraryAlbum) return;
    const title = String(album?.title || album?.name || '').trim();
    const artist = String(album?.artistName || album?.artist_name || album?.artist || '').trim();
    if (!title) return;
    let cancelled = false;
    searchQobuzAlbumIdByTitle(title, artist).then((id) => { if (id && !cancelled) setResolvedQobuzId(id); });
    return () => { cancelled = true; };
  }, [directQobuzId, album?.title, album?.name, album?.artistName, album?.artist_name, album?.artist]);

  useEffect(() => { setTrackIdResolvedAlbumId(null); }, [directQobuzId, resolvedQobuzId]);
  const qobuzAlbumId = trackIdResolvedAlbumId || directQobuzId || resolvedQobuzId;

  useEffect(() => {
    if (!qobuzAlbumId) { setQobuzFullAlbum(null); setQobuzFetchedTracks([]); setQobuzCatalogLoaded(false); return; }
    setQobuzFullAlbum(null);
    setQobuzFetchedTracks([]);
    setQobuzCatalogLoaded(false);
    useQobuzStore.setState({ albumTracks: [] });
    let cancelled = false;

    (async () => {
      try {
        const hdrs = getHeaders();
        const [albumResp, detailResp] = await Promise.all([
          fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album?albumId=${qobuzAlbumId}`, { headers: hdrs }).catch(() => null),
          fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album-detail?album_id=${qobuzAlbumId}`, { headers: hdrs }).catch(() => null),
        ]);
        if (cancelled) return;

        const albumData = albumResp?.ok ? await albumResp.json().catch(() => null) : null;
        const detailData = detailResp?.ok ? await detailResp.json().catch(() => null) : null;

        const albumObj = albumData?.album || (albumData?.id && albumData?.title ? albumData : null);
        const detailObj = detailData?.album || (detailData?.id && detailData?.title ? detailData : null) || detailData;

        const merged = { ...(detailObj || {}), ...(albumObj || {}) };

        // Preserve richer genre data from whichever source has sub_genres
        const detailGenre = detailObj?.genre;
        const albumGenre = albumObj?.genre;
        if (detailGenre?.sub_genres?.length > 0 || detailGenre?.subgenres?.length > 0) {
          merged.genre = detailGenre;
        } else if (albumGenre?.sub_genres?.length > 0 || albumGenre?.subgenres?.length > 0) {
          merged.genre = albumGenre;
        }
        // Always keep genres_list from detail if it exists
        if (detailObj?.genres_list?.length) merged.genres_list = detailObj.genres_list;
        else if (albumObj?.genres_list?.length) merged.genres_list = albumObj.genres_list;
        if (detailObj?.genres && !merged.genres) merged.genres = detailObj.genres;
        if (detailObj?.subgenres && !merged.subgenres) merged.subgenres = detailObj.subgenres;
        if (detailObj?.sub_genres && !merged.sub_genres) merged.sub_genres = detailObj.sub_genres;
        if (detailObj?.description && !merged.description) merged.description = detailObj.description;
        // Preserve genre_full from the album endpoint (raw Qobuz genre object with sub_genres)
        if (albumObj?.genre_full) merged.genre_full = albumObj.genre_full;
        // Preserve performers and track credits for the credits tab
        if (detailObj?.performers && !merged.performers) merged.performers = detailObj.performers;
        else if (albumObj?.performers && !merged.performers) merged.performers = albumObj.performers;
        if (detailObj?.trackCredits) merged.trackCredits = detailObj.trackCredits;

        if (process.env.NODE_ENV !== 'production') {
          console.debug('[AlbumDetail:qobuzFull] genre fields:', {
            albumGenre: albumObj?.genre, detailGenre: detailObj?.genre,
            mergedGenre: merged.genre, genresList: merged.genres_list,
            subgenres: merged.subgenres, sub_genres: merged.sub_genres,
          });
        }
        if (merged.id || merged.title) setQobuzFullAlbum(merged);

        // Extract tracks from the album response as a fallback when tracks prop is empty.
        // Check all possible response shapes: { tracks: { items: [...] } }, { tracks: [...] },
        // and both merged + raw response objects. Use first non-empty array found.
        const _findTracks = (...sources) => sources.find(s => Array.isArray(s) && s.length > 0) || [];
        const rawTracks = _findTracks(
          merged?.tracks?.items, merged?.tracks,
          albumObj?.tracks?.items, albumObj?.tracks,
          albumData?.tracks?.items, albumData?.tracks,
          detailData?.tracks?.items, detailData?.tracks,
        );
        console.log('[AlbumDetail] track extraction:', {
          mergedTracksType: typeof merged?.tracks, mergedTracksIsArray: Array.isArray(merged?.tracks),
          mergedTracksItemsLen: merged?.tracks?.items?.length, mergedTracksLen: Array.isArray(merged?.tracks) ? merged.tracks.length : undefined,
          rawTracksLen: rawTracks.length,
        });
        const _strip = (id) => String(id || '').replace(/^qobuz:(track|album|artist):/, '');
        if (rawTracks.length > 0) {
          const mapped = rawTracks.map((t, idx) => ({
            ...t,
            id: _strip(t.serviceTrackId || t.id || idx),
            title: t.title || `Track ${idx + 1}`,
            artistName: t.performer?.name || t.artist?.name || t.artistName || merged?.artist?.name || merged?.artistName || '',
            duration_ms: typeof t.duration === 'number' ? Math.round(t.duration * 1000) : (t.duration_ms || null),
            position: t.track_number || t.trackNumber || idx + 1,
            discNumber: t.media_number || t.discNumber || 1,
            hires: (getQualityTier(t.maximum_bit_depth || t.maxBitDepth, t.maximum_sampling_rate || t.maxSampleRate)?.tier ?? 'cd') !== 'cd',
            qualityTier: getQualityTier(t.maximum_bit_depth || t.maxBitDepth, t.maximum_sampling_rate || t.maxSampleRate),
            streamable: t.streamable !== false,
            source: 'qobuz',
            serviceTrackId: _strip(t.serviceTrackId || t.id || ''),
          }));
          setQobuzFetchedTracks(mapped);

          if (useQobuzStore.getState().albumTracks.length === 0) {
            const storeReady = mapped.map((tr) => ({
              id: tr.id,
              serviceTrackId: tr.serviceTrackId,
              title: tr.title,
              artistName: tr.artistName,
              albumTitle: merged?.title || '',
              albumId: String(merged?.id || ''),
              artistId: '',
              coverUrl: (() => {
                const explicit = merged?.image?.large || merged?.image?.small
                  || merged?.coverUrl || merged?.cover || merged?.imageUrl || '';
                if (explicit) return explicit;
                const qid = String(merged?.id || merged?.serviceAlbumId || '').trim();
                if (qid.length >= 4 && !/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(qid) && !/^\d+$/.test(qid) && !qid.includes(':'))
                  return `https://static.qobuz.com/images/covers/${qid.slice(-2)}/${qid.slice(-4, -2)}/${qid}_600.jpg`;
                return '';
              })(),
              duration: tr.duration_ms ? tr.duration_ms / 1000 : 0,
              trackNumber: tr.position || 1,
              discNumber: tr.discNumber || 1,
              hires: (getQualityTier(tr.maximum_bit_depth || tr.maxBitDepth, tr.maximum_sampling_rate || tr.maxSampleRate)?.tier ?? 'cd') !== 'cd',
              qualityTier: getQualityTier(tr.maximum_bit_depth || tr.maxBitDepth, tr.maximum_sampling_rate || tr.maxSampleRate),
              streamable: tr.streamable !== false,
              source: 'qobuz',
            }));
            useQobuzStore.setState({ albumTracks: storeReady });
          }
        }
        // Nothing came back: a short all-digit id may be a Qobuz TRACK id (old Recently Played
        // entry / "go to album" from the player). Load its album; never claim "not available".
        const badId = String(qobuzAlbumId);
        const realId = !albumObj && rawTracks.length === 0 && isTrackIdShapedAlbumId(badId) ? await resolveQobuzAlbumIdForTrackId(badId) : null;
        if (realId && !cancelled) {
          rekeyRecentAlbum(badId, realId);
          setTrackIdResolvedAlbumId(realId);
          if (window.location.pathname === `/app/album/${encodeURIComponent(badId)}`) navigate(`/app/album/${encodeURIComponent(realId)}`, { replace: true, state: { source: 'qobuz' } });
          return;
        }
      } catch {} finally {
        if (!cancelled) setQobuzCatalogLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [qobuzAlbumId, qobuzRetryNonce]);

  // Fetch artist bio — local library uses Connector enrichment; streaming uses cloud catalog.
  const albumArtistNameRaw = album?.artistName || album?.artist_name || album?.artist || '';
  useEffect(() => {
    if (!albumArtistNameRaw && !isLocalLibraryAlbum) { setArtistBio(null); return; }

    if (isLocalLibraryAlbum) {
      const bio = String(album?.artist_biography || album?.artist_bio || album?.bio || '').trim();
      setArtistBio(bio || null);
      setArtistBioLoading(!bio && !album?.enrichment_attempted_at);
      setArtistBioExpanded(false);
      return undefined;
    }

    let cancelled = false;
    setArtistBioLoading(true);
    setArtistBio(null);
    setArtistBioExpanded(false);

    const timerId = setTimeout(() => {
      if (cancelled) return;
      (async () => {
        try {
          const resp = await fetch(
            `${CLOUD_API_URL}/api/artists/${encodeURIComponent(albumArtistNameRaw)}`,
          );
          if (!resp.ok || cancelled) { if (!cancelled) setArtistBioLoading(false); return; }
          const full = await resp.json();
          if (!cancelled) {
            const bio = full?.biographyNarrative || full?.biography_narrative || full?.biography || full?.bioSummary || '';
            setArtistBio(bio ? String(bio).trim() : null);
            setArtistBioLoading(false);
          }
        } catch {
          if (!cancelled) setArtistBioLoading(false);
        }
      })();
    }, 3000);

    return () => { cancelled = true; clearTimeout(timerId); };
  }, [albumArtistNameRaw, isLocalLibraryAlbum, album?.artist_biography, album?.artist_bio, album?.bio, album?.enrichment_attempted_at]);

  // Extract a plain-text teaser from artist bio for 2-line preview
  const artistBioTeaser = useMemo(() => {
    if (!artistBio) return '';
    if (isLocalLibraryAlbum) {
      const plain = String(artistBio).replace(/\s+/g, ' ').trim();
      return plain.length > 240 ? `${plain.slice(0, 240).trim()}…` : plain;
    }
    let stripped = artistBio.replace(/\r\n/g, '\n');
    stripped = stripped.replace(/^(?:#\s+)?[^:\n]{2,80}:\s*A\s+Maestro\s+Biography\s+Overview\s*/i, '');
    stripped = stripped.replace(/\bA\s+Maestro\s+Biography\s+Overview\b\s*/gi, '');
    const lines = stripped.split('\n');
    for (const line of lines) {
      const t = line.trim();
      if (!t) continue;
      if (t.startsWith('#')) continue;
      if (t.startsWith('- ')) continue;
      if (t.includes('.') || t.length >= 60) {
        const clean = t
          .replace(/^\*\*[^*]+\*\*\s*/, '')
          .replace(/\[\[([^\]]+)\]\]/g, '$1')
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1');
        return clean.length > 400 ? clean.slice(0, 400).trim() + '…' : clean;
      }
    }
    return '';
  }, [artistBio, isLocalLibraryAlbum]);

  // Hydrate album narrative from the API when we only have a thin album object.
  // This fixes the common UX where mirror-based tracks/credits render, links work,
  // but the album description never appears because the opener didn't provide it.
  useEffect(() => {
    let cancelled = false;
    
    // Local library albums use folder paths as IDs — catalog endpoints
    // don't understand these, so skip all enrichment/hydration to avoid
    // flooding the console with 404 errors.
    if (isLocalLibraryAlbum) {
      setApiAlbum(null);
      setApiAlbumLoading(false);
      return () => {};
    }

    const existingBio = String(
      album?.biographyNarrative || album?.biography_narrative || ''
    ).trim();
    
    // If we already have a templated bio, skip hydration.
    if (isTemplateReadyAlbumText(existingBio)) {
      setApiAlbum(null);
      setApiAlbumLoading(false);
      return () => {};
    }

    const albumTitle = album?.title || '';
    const artistName = album?.artistName || album?.artist_name || album?.artist || '';
    
    // Need either an ID or title+artist for hydration
    if (!albumApiId && !albumTitle) {
      setApiAlbum(null);
      setApiAlbumLoading(false);
      return () => {};
    }

    setApiAlbumLoading(true);
    
    const tryFetchById = async () => {
      if (!albumApiId) return null;
      try {
        const r = await fetch(`${CLOUD_API_URL}/api/albums/${encodeURIComponent(albumApiId)}`);
        if (!r.ok) return null;
        const j = await r.json();
        if (j && (j.title || j.id)) return j;
        return null;
      } catch {
        return null;
      }
    };
    
    const trySearchByTitleArtist = async () => {
      if (!albumTitle) return null;
      try {
        const q = artistName ? `${albumTitle} ${artistName}` : albumTitle;
        const r = await fetch(`${CLOUD_API_URL}/api/search?q=${encodeURIComponent(q)}&limit=5&_cb=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return null;
        const data = await r.json();
        const albums = Array.isArray(data?.albums) ? data.albums : [];
        const match = albums.find(a => 
          a?.title?.toLowerCase() === albumTitle.toLowerCase() ||
          a?.title?.toLowerCase().includes(albumTitle.toLowerCase())
        ) || albums[0];
        if (!match?.id) return null;
        const fullRes = await fetch(`${CLOUD_API_URL}/api/albums/${encodeURIComponent(match.id)}?_cb=${Date.now()}`, { cache: 'no-store' });
        if (!fullRes.ok) return null;
        const full = await fullRes.json();
        if (full && (full.title || full.id)) return full;
        return null;
      } catch {
        return null;
      }
    };
    
    (async () => {
      // First try by ID
      let result = await tryFetchById();
      // If that fails, try searching by title/artist
      if (!result && albumTitle) {
        result = await trySearchByTitleArtist();
      }
      if (cancelled) return;
      if (result) setApiAlbum(result);
      setApiAlbumLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [albumApiId, album?.title, album?.artistName, album?.artist_name, album?.artist, album?.biographyNarrative, album?.biography_narrative]);

  // Fetch mirror data including back cover
  useEffect(() => {
    let cancelled = false;
    if (!releaseGroupMbid) {
      setMirrorRelease(null);
      setMirrorTracks(null);
      setMirrorCredits(null);
      setMirrorError(null);
      setMirrorLoading(false);
      setBackCoverUrl(null);
      return () => {};
    }

    setMirrorLoading(true);
    setMirrorError(null);
    
    Promise.all([
      fetch(`${CLOUD_API_URL}/api/mirror/releases/${encodeURIComponent(releaseGroupMbid)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${CLOUD_API_URL}/api/mirror/releases/${encodeURIComponent(releaseGroupMbid)}/tracks`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${CLOUD_API_URL}/api/mirror/releases/${encodeURIComponent(releaseGroupMbid)}/credits`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      // Fetch back cover from Cover Art Archive
      fetch(`https://coverartarchive.org/release-group/${encodeURIComponent(releaseGroupMbid)}`).then(r => r.ok ? r.json() : null).catch(() => null),
    ])
      .then(([rel, tr, cr, caa]) => {
        if (cancelled) return;
        setMirrorRelease(rel?.release || null);
        setMirrorTracks(tr || null);
        setMirrorCredits(cr?.credits || null);
        
        // Find back cover from Cover Art Archive response
        if (caa?.images) {
          const backImage = caa.images.find(img => img.back === true || img.types?.includes('Back'));
          if (backImage?.image) {
            setBackCoverUrl(backImage.image.replace('http://', 'https://'));
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setMirrorError('Failed to load album data');
      })
      .finally(() => {
        if (cancelled) return;
        setMirrorLoading(false);
      });

    return () => { cancelled = true; };
  }, [releaseGroupMbid]);

  // Fetch FULL recording details when track is expanded (not just credits)
  const fetchRecordingCredits = useCallback(async (recordingMbid) => {
    const id = String(recordingMbid || '').trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return;
    if (recordingCreditsByMbid[id] || recordingCreditsLoading[id]) return;
    
    setRecordingCreditsLoading((prev) => ({ ...prev, [id]: true }));
    try {
      // Use /full endpoint for rich data (instruments, dates, studio, composers)
      const res = await fetch(`${CLOUD_API_URL}/api/mirror/releases/recording/${encodeURIComponent(id)}/full`);
      const j = await res.json().catch(() => null);
      if (res.ok && j?.success && j?.recording) {
        setRecordingCreditsByMbid((prev) => ({ ...prev, [id]: j.recording }));
      } else {
        // Fallback to basic credits endpoint
        const fallbackRes = await fetch(`${CLOUD_API_URL}/api/mirror/releases/recording/${encodeURIComponent(id)}/credits`);
        const fallbackJ = await fallbackRes.json().catch(() => null);
        if (fallbackRes.ok && fallbackJ?.success && fallbackJ?.credits) {
          setRecordingCreditsByMbid((prev) => ({ ...prev, [id]: fallbackJ.credits }));
        }
      }
    } catch (e) {
      console.error('[album-recording] credits_error', { id, error: String(e?.message || e) });
    } finally {
      setRecordingCreditsLoading((prev) => ({ ...prev, [id]: false }));
    }
  }, [recordingCreditsByMbid, recordingCreditsLoading]);

  // Mirror recording lookup
  const mirrorRecordingByPosition = useMemo(() => {
    const out = new Map();
    const list = Array.isArray(mirrorTracks?.tracks?.tracks) ? mirrorTracks.tracks.tracks : [];
    for (const t of list) {
      const pos = Number(t?.position || t?.track_number || 0) || 0;
      const rec = String(t?.recording_mbid || '').trim();
      if (pos > 0 && rec) out.set(pos, rec);
    }
    return out;
  }, [mirrorTracks]);

  // Parse album fields
  const parseField = (camel, snake, defaultVal = null) => {
    const val = album[camel] || album[snake];
    if (!val) return defaultVal;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { return val; }
    }
    return val;
  };
  
  const studioInfo = parseField('studioInfo', 'studio_info', {});
  const awards = parseField('awards', 'awards', []);
  const charts = parseField('charts', 'charts', []);
  const credits = parseField('credits', 'credits', {});
  
  const qobuzDescription = typeof qobuzFullAlbum?.description === 'object'
    ? (qobuzFullAlbum.description?.summary || qobuzFullAlbum.description?.full || '')
    : (qobuzFullAlbum?.description || '');
  const rawBiography =
    apiAlbum?.biographyNarrative ||
    apiAlbum?.biography_narrative ||
    apiAlbum?.description ||
    album.biographyNarrative ||
    album.biography_narrative ||
    album.description ||
    (qobuzDescription.length > 20 ? qobuzDescription : null) ||
    (artistBio || null);

  const biographyNarrative = rawBiography;
  const isTemplateBio = isTemplateReadyAlbumText(biographyNarrative);
  const bioToRender = isTemplateBio ? String(biographyNarrative || '').trim() : String(rawBiography || '').trim();
  const cleanedBio = useMemo(() => {
    if (!bioToRender) return '';
    if (isTemplateBio) return cleanBioText(bioToRender);
    const cleaned = cleanAlbumDescription(bioToRender);
    if (looksLikeWikipediaDump(cleaned)) return extractSafeAlbumTeaser(cleaned);
    return cleaned;
  }, [bioToRender, isTemplateBio]);
  const { text: bioTruncated, truncated: bioWasTruncated } = useMemo(() => {
    if (!cleanedBio || isTemplateBio) return { text: cleanedBio, truncated: false };
    return truncateToWords(cleanedBio, 1000);
  }, [cleanedBio, isTemplateBio]);
  const templateCollapsedTeaser = useMemo(() => {
    if (!isTemplateBio || !bioToRender) return '';
    return templateBioCollapsedTeaser(bioToRender);
  }, [isTemplateBio, bioToRender]);

  // Global policy: if the album bio is not template-ready, trigger enrichment and keep trying for a short window.
  // We still render a safe teaser immediately so the page never feels "broken".
  const enrichKickRef = React.useRef(new Map()); // albumApiId -> ts
  useEffect(() => {
    if (!albumApiId) return;
    if (isTemplateBio) return;
    if (isLocalLibraryAlbum) return;
    const id = String(albumApiId).trim();
    if (!id) return;

    const last = enrichKickRef.current.get(id) || 0;
    const nowTs = Date.now();
    // Throttle: kick at most once per 60s per album id.
    if (nowTs - last < 60000) return;
    enrichKickRef.current.set(id, nowTs);

    let cancelled = false;
    (async () => {
      const sleepMs = (ms) => new Promise((r) => setTimeout(r, ms));

      const enrichUrl = `${CLOUD_API_URL}/api/enrich/album/${encodeURIComponent(id)}`;
      fetch(enrichUrl, { method: 'GET' }).catch(() => {});

      const scheduleMs = [2000, 5000, 10000, 15000];
      const start = Date.now();
      for (let i = 0; i < scheduleMs.length; i++) {
        const elapsed = Date.now() - start;
        const waitMs = Math.max(0, scheduleMs[i] - elapsed);
        await sleepMs(waitMs);
        if (cancelled) return;
        try {
          const pollUrl = `${CLOUD_API_URL}/api/albums/${encodeURIComponent(id)}?_cb=${Date.now()}`;
          const r = await fetch(pollUrl, { cache: 'no-store' });
          if (!r.ok) {
            const raw = await r.text().catch(() => '');
            console.error('[album-enrich] poll_failed', { id, attempt: i + 1, status: r.status, body: raw });
            continue;
          }
          const j = await r.json().catch(() => null);
          if (j && (j.title || j.id)) {
            if (!cancelled) setApiAlbum(j);
            const bio = String(j?.biographyNarrative || j?.biography_narrative || '').trim();
            if (isTemplateReadyAlbumText(bio)) return;
          }
        } catch (e) {
          console.error('[album-enrich] poll_error', { id, attempt: i + 1, error: String(e?.message || e) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [albumApiId, isTemplateBio]);
  const recordingHistory = album.recordingHistory || album.recording_history;
  const bandEraContext = album.bandEraContext || album.band_era_context;
  // Cover-art priority chain — same resolution as library AlbumGrid / LocalAlbumCover.
  const embHash = normalizeCoverHash(albumWithIndexCover.embedded_cover_hash);
  const folHash = normalizeCoverHash(albumWithIndexCover.folder_cover_hash);
  const extHash = normalizeCoverHash(albumWithIndexCover.external_cover_hash);
  const thumbHash = pickCoverArtThumbHash(albumWithIndexCover);
  const bareAlbumKey = albumWithIndexCover?.album_key || albumWithIndexCover?.id || '';
  const detailHash = normalizeCoverHash(getExternalDetailHash(bareAlbumKey));
  const primaryEmbHash = embHash || detailHash;
  const albumFolderPath = useMemo(() => {
    const trackPath = tracks?.[0]?.file_path || album?.tracks?.[0]?.file_path || null;
    return pickAlbumFolderPath({
      library_folder_path: albumWithIndexCover?.library_folder_path,
      sample_file_path: albumWithIndexCover?.sample_file_path || trackPath,
      file_path: albumWithIndexCover?.file_path || trackPath,
    });
  }, [
    albumWithIndexCover?.library_folder_path,
    albumWithIndexCover?.sample_file_path,
    albumWithIndexCover?.file_path,
    albumWithIndexCover?.tracks,
    tracks,
  ]);
  const indexHashesEmpty = !embHash && !folHash && !extHash;
  const needsLazyHashResolve = isLocalLibraryAlbum && indexHashesEmpty && shouldUseLocalLibraryIpc();
  const deferEnrichedCover = shouldDeferEnrichedCoverForLocalAlbum({
    isLocalLibraryAlbum,
    indexHashesEmpty,
    albumFolderPath,
    detailHashPending: needsLazyHashResolve && !isExternalCoverKnownArtless(bareAlbumKey),
  });
  const localCoverHash = embHash || folHash || extHash;
  const hasLocalArtSource = !!(localCoverHash || albumFolderPath);

  const enrichedCoverAllowed = useMemo(() => mayUseEnrichedCoverUrl({
    artworkSource: album?.artwork_source || album?.winning_source,
    embeddedCoverHash: embHash,
    folderCoverHash: folHash,
  }), [
    album?.artwork_source,
    album?.winning_source,
    embHash,
    folHash,
  ]);

  const syncDetailCoverFromCache = useCallback((primaryEmb, fol, ext, thumb) => {
    for (const h of [primaryEmb, fol, ext, thumb]) {
      if (h && hasResolvedCoverInCache(h)) return getCoverFromCache(h) || null;
    }
    return null;
  }, []);

  const [externalResolveRev, setExternalResolveRev] = useState(0);
  useEffect(() => {
    if (!bareAlbumKey) return undefined;
    return subscribeExternalCoverResolve(bareAlbumKey, () => {
      setExternalResolveRev((n) => n + 1);
    });
  }, [bareAlbumKey]);

  const [localBlobUrl, setLocalBlobUrl] = useState(() => {
    const cached = resolvedLocalCoverUrl(syncDetailCoverFromCache(primaryEmbHash, folHash, extHash, thumbHash));
    if (cached) return cached;
    const rawLegacy = getCoverFromCache(thumbHash) ?? getCoverFromCache(embHash) ?? getCoverFromCache(folHash);
    if (rawLegacy !== undefined) return resolvedLocalCoverUrl(rawLegacy);
    return resolvedLocalCoverUrl(resolveLocalAlbumCoverSync(album));
  });
  const [wallCoverFallback, setWallCoverFallback] = useState(null);
  const [localResolutionComplete, setLocalResolutionComplete] = useState(
    !isLocalLibraryAlbum || (!hasLocalArtSource && !deferEnrichedCover),
  );
  const coverLoadedRef = useRef(false);
  const [cacheRevision, setCacheRevision] = useState(0);

  useEffect(() => {
    coverLoadedRef.current = false;
    setLocalResolutionComplete(!isLocalLibraryAlbum || (!hasLocalArtSource && !deferEnrichedCover));
  }, [
    bareAlbumKey,
    hasLocalArtSource,
    deferEnrichedCover,
    isLocalLibraryAlbum,
    primaryEmbHash,
    detailHash,
    externalResolveRev,
    cacheRevision,
    album?.cover,
    album?.artworkUrl,
    album?.artwork_url,
  ]);

  useEffect(() => {
    if (!primaryEmbHash && !folHash && !extHash && !thumbHash) return undefined;
    return subscribeCoverCacheLoaded((hash) => {
      if (hash === primaryEmbHash || hash === folHash || hash === extHash || hash === thumbHash) {
        setCacheRevision((n) => n + 1);
      }
    });
  }, [primaryEmbHash, folHash, extHash, thumbHash]);

  useEffect(() => {
    if (!needsLazyHashResolve || !bareAlbumKey) return;
    if (primaryEmbHash || folHash || extHash) return;
    if (isExternalCoverKnownArtless(bareAlbumKey)) return;
    requestExternalAlbumCoverHash(bareAlbumKey);
  }, [needsLazyHashResolve, bareAlbumKey, primaryEmbHash, folHash, extHash, externalResolveRev]);

  useEffect(() => {
    if (!isLocalLibraryAlbum) return;

    const cachedUrl = resolvedLocalCoverUrl(
      syncDetailCoverFromCache(primaryEmbHash, folHash, extHash, thumbHash),
    );
    if (cachedUrl) {
      setLocalBlobUrl(cachedUrl);
      coverLoadedRef.current = true;
      setLocalResolutionComplete(true);
      return;
    }

    const rawLegacy = getCoverFromCache(thumbHash) ?? getCoverFromCache(embHash) ?? getCoverFromCache(folHash);
    if (rawLegacy !== undefined) {
      setLocalBlobUrl(resolvedLocalCoverUrl(rawLegacy));
      coverLoadedRef.current = true;
      setLocalResolutionComplete(true);
      return;
    }

    const syncUrl = resolvedLocalCoverUrl(resolveLocalAlbumCoverSync(albumWithIndexCover));
    if (syncUrl) {
      setLocalBlobUrl(syncUrl);
      coverLoadedRef.current = true;
      setLocalResolutionComplete(true);
      return;
    }

    if (coverLoadedRef.current) return;

    // Match AlbumGrid.jsx:1006-1008 — wait for session detailHash before resolving.
    if (!primaryEmbHash && !folHash && !extHash) {
      if (needsLazyHashResolve && !isExternalCoverKnownArtless(bareAlbumKey)) {
        return;
      }
    }

    let cancelled = false;
    coverLoadedRef.current = true;
    (async () => {
      try {
        const url = resolvedLocalCoverUrl(await resolveLocalAlbumCoverDisplay({
          primaryEmbeddedHash: primaryEmbHash,
          folderHash: folHash,
          externalHash: extHash,
          album: albumWithIndexCover,
        }));
        if (!cancelled) {
          setLocalBlobUrl(url);
          setWallCoverFallback(url);
        }
      } finally {
        if (!cancelled) setLocalResolutionComplete(true);
      }
    })();
    return () => { cancelled = true; };
  }, [
    isLocalLibraryAlbum,
    primaryEmbHash,
    folHash,
    extHash,
    thumbHash,
    albumFolderPath,
    bareAlbumKey,
    albumWithIndexCover,
    needsLazyHashResolve,
    externalResolveRev,
    cacheRevision,
    syncDetailCoverFromCache,
  ]);

  const handleHeroCoverProxyRecover = useCallback(async () => {
    if (!isCoverIPCAvailable()) return null;
    if (!(primaryEmbHash || folHash || extHash || thumbHash)) return null;
    coverLoadedRef.current = false;
    const url = resolvedLocalCoverUrl(await resolveLocalAlbumCoverDisplay({
      primaryEmbeddedHash: primaryEmbHash,
      folderHash: folHash,
      externalHash: extHash,
      album: albumWithIndexCover,
    }));
    if (url) {
      setLocalBlobUrl(url);
      setLocalResolutionComplete(true);
    }
    return url;
  }, [primaryEmbHash, folHash, extHash, thumbHash, albumWithIndexCover]);

  const localCoverUrl = resolvedLocalCoverUrl(localBlobUrl);
  const albumObjectCoverUrl = pickValidAlbumObjectCoverUrl(
    album?.cover,
    album?.artworkUrl,
    album?.artwork_url,
  );
  // Construct Qobuz CDN URL from serviceAlbumId as a final fallback
  // Pattern: https://static.qobuz.com/images/covers/{id[-2:]}/{id[-4:-2]}/{id}_600.jpg
  const constructedQobuzCover = (() => {
    const id = qobuzAlbumId;
    if (!id || id.length < 4 || /^[0-9a-f]{8}-[0-9a-f]{4}/.test(id)) return null;
    return `https://static.qobuz.com/images/covers/${id.slice(-2)}/${id.slice(-4, -2)}/${id}_600.jpg`;
  })();

  const onlineCoverUrl =
    album.coverUrl ||
    album.cover_url ||
    album.coverUrlLarge ||
    album.cover_url_large ||
    album.artworkUrl ||
    album.artwork_url ||
    album.cover_art_url ||
    album.cover ||
    album.imageUrl ||
    album.image?.large ||
    album.image?.small ||
    album.image?.thumbnail ||
    qobuzFullAlbum?.image?.large ||
    qobuzFullAlbum?.image?.small ||
    qobuzFullAlbum?.image?.thumbnail ||
    constructedQobuzCover ||
    null;
  const safeOnlineCoverUrl = isRenderableCoverUrl(onlineCoverUrl)
    ? (String(onlineCoverUrl).startsWith('http://')
      ? String(onlineCoverUrl).replace(/^http:\/\//i, 'https://')
      : onlineCoverUrl)
    : null;
  const enrichedOnlineCover = enrichedCoverAllowed ? safeOnlineCoverUrl : null;
  // Phase 2D — Library Inspector override hook on top of the default chain.
  // user_override_source pins which source wins; missing override falls
  // through to the legacy local→online priority.
  const overrideSource = album.user_override_source;
  const coverUrl = useMemo(() => {
    if (!isLocalLibraryAlbum) {
      return localCoverUrl || safeOnlineCoverUrl || null;
    }
    if (overrideSource === 'embedded' || overrideSource === 'folder') {
      return localCoverUrl || enrichedOnlineCover || null;
    }
    if (overrideSource === 'online') {
      return enrichedOnlineCover || localCoverUrl || null;
    }
    if (overrideSource === 'none') {
      return null;
    }
    return pickLocalLibraryDisplayCover({
      localCoverUrl,
      albumObjectCoverUrl,
      onlineCoverUrl: enrichedOnlineCover,
      overrideSource,
      hasLocalArtSource: hasLocalArtSource || deferEnrichedCover,
      localResolutionComplete,
      isRemoteTunnel: false,
      gridPreferPlaceholderOverEnriched: false,
    });
  }, [
    isLocalLibraryAlbum,
    localCoverUrl,
    albumObjectCoverUrl,
    safeOnlineCoverUrl,
    enrichedOnlineCover,
    overrideSource,
    hasLocalArtSource,
    deferEnrichedCover,
    localResolutionComplete,
  ]);


  // Genre tags — merge from normalizeAlbum() output AND live qobuzFullAlbum data
  const genres = useMemo(() => {
    const seen = new Set();
    const dedup = (list) => list.filter((g) => {
      if (!g.name) return false;
      const k = g.name.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    const flattenG = (obj) => {
      const out = [];
      if (!obj || typeof obj !== 'object') return out;
      if (obj.name) out.push({ id: obj.id ?? null, name: String(obj.name).trim(), source: album.source || null });
      const subs = obj.sub_genres || obj.subgenres || obj.children;
      if (Array.isArray(subs)) {
        for (const sg of subs) {
          if (typeof sg === 'string') out.push({ id: null, name: sg.trim(), source: album.source || null });
          else if (sg && typeof sg === 'object') out.push(...flattenG(sg));
        }
      }
      return out;
    };

    const toGenres = (src) => {
      if (!src) return [];
      if (Array.isArray(src)) {
        const out = [];
        for (const g of src) {
          if (typeof g === 'string') out.push({ id: null, name: g.trim(), source: album.source || null });
          else if (g && typeof g === 'object') out.push(...flattenG(g));
        }
        return out;
      }
      if (typeof src === 'object') return flattenG(src);
      if (typeof src === 'string') return src.split(/[,;]/).map(s => s.trim()).filter(Boolean).map(n => ({ id: null, name: n, source: album.source || null }));
      return [];
    };

    const all = [
      ...toGenres(album.genre),
      ...toGenres(album.genres),
      ...toGenres(album.genres_list),
      ...toGenres(album.subgenres),
      ...toGenres(album.sub_genres),
      ...toGenres(album.tags),
      ...(qobuzFullAlbum ? [
        ...toGenres(qobuzFullAlbum.genre),
        ...toGenres(qobuzFullAlbum.genre_full),
        ...toGenres(qobuzFullAlbum.genres),
        ...toGenres(qobuzFullAlbum.genres_list),
        ...toGenres(qobuzFullAlbum.subgenres),
        ...toGenres(qobuzFullAlbum.sub_genres),
      ] : []),
    ];
    return dedup(all);
  }, [album, qobuzFullAlbum]);

  const [validatedGenres, setValidatedGenres] = useState([]);
  useEffect(() => {
    if (!genres.length) {
      setValidatedGenres((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    if (isLocalLibraryAlbum) {
      setValidatedGenres((prev) => {
        if (prev.length === genres.length && prev.every((g, i) => g.name === genres[i]?.name)) {
          return prev;
        }
        return genres;
      });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/validate-genres`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getHeaders() },
          body: JSON.stringify({ genres: genres.map(g => ({ id: g.id, name: g.name })) }),
        });
        if (!resp.ok || cancelled) { if (!cancelled) setValidatedGenres(genres); return; }
        const data = await resp.json();
        const validSet = new Set();
        for (const v of (data.validated || [])) {
          if (v.hasAlbums) validSet.add(v.name?.toLowerCase());
        }
        if (!cancelled) setValidatedGenres(genres.filter(g => validSet.has(g.name?.toLowerCase())));
      } catch {
        if (!cancelled) setValidatedGenres(genres);
      }
    })();
    return () => { cancelled = true; };
  }, [genres, isLocalLibraryAlbum]);

  // Compute tracks
  const mirrorTrackList = useMemo(() => {
    const list = Array.isArray(mirrorTracks?.tracks?.tracks) ? mirrorTracks.tracks.tracks : [];
    return list.map((t, idx) => ({
      id: String(t?.recording_mbid || t?.track_mbid || idx),
      title: String(t?.name || '').trim() || `Track ${idx + 1}`,
      duration_ms: typeof t?.duration_ms === 'number' ? t.duration_ms : (typeof t?.length === 'number' ? t.length : null),
      position: Number(t?.position || idx + 1),
      recordingMbid: String(t?.recording_mbid || '').trim() || null,
      format: t?.format || null,
      discNumber: t?.disc_number || t?.discNumber || null,
    }));
  }, [mirrorTracks]);

  const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const _isPlayableTrack = (t) => {
    if (t?.streamUrl) return true;
    if (t?.file_path) return true;
    const svcId = String(t?.serviceTrackId || '').trim();
    if (svcId && !_UUID_RE.test(svcId)) return true;
    if (t?.source === 'qobuz' || t?.source === 'local' || t?.source === 'library' || t?.source === 'local_library') return true;
    const id = String(t?.id || '').trim();
    if (id && !_UUID_RE.test(id) && /^\d+$/.test(id)) return true;
    return false;
  };

  const displayTracks = useMemo(() => {
    const base = Array.isArray(tracks) && tracks.length > 0
      ? tracks
      : (qobuzFetchedTracks.length > 0 ? qobuzFetchedTracks : []);
    return base.map((t, idx) => {
      const durationMs =
        typeof t?.durationMs === 'number' ? t.durationMs :
        typeof t?.duration_ms === 'number' ? t.duration_ms :
        typeof t?.duration === 'number' ? Math.round(t.duration * 1000) :
        typeof t?.durationSeconds === 'number' ? Math.round(t.durationSeconds * 1000) :
        typeof t?.length === 'number' ? t.length : null;
      const title = String(t?.title || t?.name || '').trim() || `Track ${idx + 1}`;
      const pos = typeof t?.position === 'number' ? t.position : (idx + 1);
      const recordingMbid = String(t?.recordingMbid || t?.recording_mbid || '').trim() || mirrorRecordingByPosition.get(pos) || null;
      return { 
        ...t, 
        title, 
        duration_ms: durationMs, 
        position: pos, 
        recordingMbid,
        format: t?.format || mirrorTrackList[idx]?.format || null,
        discNumber: t?.discNumber || t?.disc_number || mirrorTrackList[idx]?.discNumber || null,
      };
    }).filter(_isPlayableTrack);
  }, [tracks, qobuzFetchedTracks, mirrorTrackList, mirrorRecordingByPosition]);

  const qobuzTrackStreamability = useMemo(() => {
    const sourceTracks = qobuzFetchedTracks.length > 0 ? qobuzFetchedTracks : displayTracks;
    return countStreamableTracks(sourceTracks);
  }, [qobuzFetchedTracks, displayTracks]);

  const streamingCatalogUnavailable = useMemo(() => {
    if (isLocalLibraryAlbum || !qobuzAlbumId || !qobuzCatalogLoaded) return false;
    // #11212: a failed/empty album fetch is missing data, never a region verdict.
    return isQobuzAlbumUnavailableHere(qobuzFullAlbum?.rights, qobuzTrackStreamability, qobuzFullAlbum?.streamable);
  }, [isLocalLibraryAlbum, qobuzAlbumId, qobuzCatalogLoaded, qobuzTrackStreamability, qobuzFullAlbum]);

  const albumForPlay = useMemo(() => albumForPlayback(album, qobuzFullAlbum?.id, isLocalLibraryAlbum), [album, qobuzFullAlbum?.id, isLocalLibraryAlbum]);

  const localLibraryMatch = useMemo(() => {
    if (!streamingCatalogUnavailable) return null;
    return findLocalAlbumMatch(
      album?.title || album?.name || qobuzFullAlbum?.title,
      album?.artistName || album?.artist_name || album?.artist || qobuzFullAlbum?.artist?.name,
    );
  }, [streamingCatalogUnavailable, album, qobuzFullAlbum]);

  useEffect(() => {
    if (!streamingCatalogUnavailable || !qobuzAlbumId) return;
    logStreamingAlbumUnavailable(
      String(qobuzAlbumId),
      album?.source || 'qobuz',
      { totalTracks: qobuzTrackStreamability.total, streamableTracks: qobuzTrackStreamability.streamable },
    );
  }, [streamingCatalogUnavailable, qobuzAlbumId, album?.source, qobuzTrackStreamability]);

  // Handle track expansion and auto-fetch credits
  const handleToggleTrackExpand = useCallback((index, recordingMbid) => {
    if (expandedTrackIndex === index) {
      setExpandedTrackIndex(null);
    } else {
      setExpandedTrackIndex(index);
      if (recordingMbid) {
        fetchRecordingCredits(recordingMbid);
      }
    }
  }, [expandedTrackIndex, fetchRecordingCredits]);

  // Use credits from API response first, then fallback to separately fetched mirrorCredits,
  // then parse Qobuz performers string as a last resort
  const albumCreditsByRole = useMemo(() => {
    const apiCredits = album?.mirrorCredits || album?.mirror_credits;
    if (apiCredits && typeof apiCredits === 'object' && Object.keys(apiCredits).length > 0) {
      return apiCredits;
    }
    const byRole = mirrorCredits?.credits_by_role || mirrorCredits?.creditsByRole || null;
    if (byRole && typeof byRole === 'object' && Object.keys(byRole).length > 0) return byRole;

    // Parse Qobuz performers string: "Name, Role - Name2, Role2"
    const rawPerformers = qobuzFullAlbum?.performers;
    if (rawPerformers && typeof rawPerformers === 'string') {
      const roleMap = {};
      const entries = rawPerformers.split(' - ').map(s => s.trim()).filter(Boolean);
      for (const entry of entries) {
        const parts = entry.split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length < 2) continue;
        const name = parts[0];
        for (let i = 1; i < parts.length; i++) {
          const roleName = parts[i]
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/^Main\s*Artist$/i, 'Primary Artist')
            .replace(/^Associated\s*Performer$/i, 'Performer')
            .replace(/^Composer\s*Lyricist$/i, 'Songwriter')
            .replace(/^Featured\s*Artist$/i, 'Featured')
            .replace(/^Studio\s*Personnel$/i, 'Studio')
            .trim();
          if (!roleName) continue;
          if (!roleMap[roleName]) roleMap[roleName] = [];
          if (!roleMap[roleName].some(e => e.artist_name === name)) {
            roleMap[roleName].push({ artist_name: name, artist_mbid: null });
          }
        }
      }

      // Also merge per-track credits (composers, performers)
      const trackCreds = qobuzFullAlbum?.trackCredits;
      if (Array.isArray(trackCreds)) {
        for (const tc of trackCreds) {
          if (tc.composer?.name) {
            if (!roleMap['Composer']) roleMap['Composer'] = [];
            if (!roleMap['Composer'].some(e => e.artist_name === tc.composer.name)) {
              roleMap['Composer'].push({ artist_name: tc.composer.name, artist_mbid: null });
            }
          }
          if (tc.performers && typeof tc.performers === 'string') {
            const tEntries = tc.performers.split(' - ').map(s => s.trim()).filter(Boolean);
            for (const te of tEntries) {
              const tParts = te.split(',').map(s => s.trim()).filter(Boolean);
              if (tParts.length < 2) continue;
              const tName = tParts[0];
              for (let i = 1; i < tParts.length; i++) {
                const tRole = tParts[i]
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/^Main\s*Artist$/i, 'Primary Artist')
                  .replace(/^Associated\s*Performer$/i, 'Performer')
                  .replace(/^Composer\s*Lyricist$/i, 'Songwriter')
                  .replace(/^Featured\s*Artist$/i, 'Featured')
                  .replace(/^Studio\s*Personnel$/i, 'Studio')
                  .trim();
                if (!tRole) continue;
                if (!roleMap[tRole]) roleMap[tRole] = [];
                if (!roleMap[tRole].some(e => e.artist_name === tName)) {
                  roleMap[tRole].push({ artist_name: tName, artist_mbid: null });
                }
              }
            }
          }
        }
      }

      if (Object.keys(roleMap).length > 0) return roleMap;
    }
    return null;
  }, [album?.mirrorCredits, album?.mirror_credits, mirrorCredits, qobuzFullAlbum]);
  
  // Also expose the merged credits from API
  const apiMergedCredits = useMemo(() => {
    return album?.credits && typeof album.credits === 'object' ? album.credits : null;
  }, [album?.credits]);

  const {
    creditsByRole: hookCredits,
    loading: creditsLoading,
    refetch: refetchCredits,
    sources: creditsSources,
  } = useAlbumCredits({
    albumId: albumApiId,
    albumTitle: album?.title || '',
    artistName: album?.artistName || album?.artist_name || album?.artist || '',
    qobuzAlbumId,
    qobuzFullAlbum,
    releaseGroupMbid,
    apiAlbum,
    album,
  });

  const totalDurationMs = useMemo(() => {
    const fromTracks = displayTracks.reduce((sum, t) => sum + (t.duration_ms || 0), 0);
    if (fromTracks > 0) return fromTracks;
    const albumDurSec = album.duration || qobuzFullAlbum?.duration;
    if (typeof albumDurSec === 'number' && albumDurSec > 0) return Math.round(albumDurSec * 1000);
    return 0;
  }, [displayTracks, album.duration, qobuzFullAlbum?.duration]);

  const releaseDate = album.releaseDate || album.release_date || album.release_year || album.year ||
    qobuzFullAlbum?.released_at || qobuzFullAlbum?.release_date_original;
  const formattedReleaseDate = useMemo(() => {
    if (!releaseDate) return null;
    if (typeof releaseDate === 'number') {
      if (releaseDate > 9999) return null;
      return String(releaseDate);
    }
    const s = String(releaseDate).trim();
    if (/^\d{5,}$/.test(s)) return null;
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    if (d.getFullYear() < 1800 || d.getFullYear() > 2100) return null;
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  }, [releaseDate]);

  const albumType = album.releaseType || album.release_type || mirrorRelease?.type || 'Album';
  
  const albumTitle = album.title && !/^\d{5,}$/.test(String(album.title).trim()) ? album.title : (qobuzFullAlbum?.title || apiAlbum?.title || '');
  const albumArtist = album.artistName || album.artist_name || album.artist || 'Unknown Artist';
  const trackCount = album.tracksCount || album.trackCount || album.tracks_count || qobuzFullAlbum?.tracks_count || displayTracks.length;
  const albumGenre = (genres.length > 0 ? genres[0]?.name : null) || (typeof album.genre === 'string' ? album.genre : album.genre?.name) || qobuzFullAlbum?.genre?.name || qobuzFullAlbum?.genre || null;
  const albumLabel = album.label?.name || album.label || album.recordLabel || qobuzFullAlbum?.label?.name || qobuzFullAlbum?.label || apiAlbum?.label?.name || apiAlbum?.label || mirrorRelease?.label;
  const maxBd = album.maxBitDepth || album.maximum_bit_depth || qobuzFullAlbum?.maximum_bit_depth || qobuzFullAlbum?.maxBitDepth || apiAlbum?.maximum_bit_depth || apiAlbum?.maxBitDepth || apiAlbum?.bit_depth || null;
  const maxSr = album.maxSampleRate || album.maximum_sampling_rate || qobuzFullAlbum?.maximum_sampling_rate || qobuzFullAlbum?.maxSampleRate || apiAlbum?.maximum_sampling_rate || apiAlbum?.maxSampleRate || apiAlbum?.sample_rate || null;
  const albumTier = getQualityTier(maxBd, maxSr);
  const isHiRes = albumTier?.tier === 'hi-res' || albumTier?.tier === 'med-res';
  const isQobuzSource = album.source === 'qobuz' || !!qobuzAlbumId;
  const albumFormat = album.primaryFormat || album.format || album.audioFormat || apiAlbum?.primaryFormat || apiAlbum?.audioFormat || (isQobuzSource ? 'FLAC' : null) || (isHiRes ? 'FLAC' : null);
  const isAlbumFav = checkFav('album', String(album.id || album.serviceAlbumId || ''));

  const showAlbumToast = useCallback((msg) => {
    setAlbumToast(msg);
    if (albumToastTimer.current) clearTimeout(albumToastTimer.current);
    albumToastTimer.current = setTimeout(() => setAlbumToast(null), 2500);
  }, []);

  const handleAddAllToQueue = useCallback(() => {
    if (!displayTracks.length) return;
    const items = displayTracks.map((t) => ({
      track: {
        id: t.id || t.serviceTrackId,
        title: t.title || 'Unknown',
        artist: t.artistName || t.artist || albumArtist,
        album: albumTitle,
        artworkUrl: coverUrl,
        duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0),
        streamUrl: t.streamUrl || '',
        source: t.source || album.source || 'maestro',
        qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || t.id,
      },
    }));
    useMaestroStore.getState().addToQueue(items);
    showAlbumToast(`${displayTracks.length} tracks added to queue`);
  }, [displayTracks, albumTitle, coverUrl, albumArtist, album.source, showAlbumToast]);

  const handleAddTrackToQueue = useCallback((t) => {
    useMaestroStore.getState().addToQueue([{
      track: {
        id: t.id || t.serviceTrackId,
        title: t.title || 'Unknown',
        artist: t.artistName || t.artist || albumArtist,
        album: albumTitle,
        artworkUrl: coverUrl,
        duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0),
        streamUrl: t.streamUrl || '',
        source: t.source || album.source || 'maestro',
        qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || t.id,
      },
    }]);
  }, [albumTitle, coverUrl, albumArtist, album.source]);

  const handleCopyAlbumLink = useCallback(() => {
    const url = window.location.href;
    const text = `${albumTitle} by ${albumArtist}\n${url}`;
    navigator.clipboard?.writeText(text).then(() => {
      showAlbumToast('Copied to clipboard');
    }).catch(() => {});
    setAlbumMenuOpen(false);
  }, [albumTitle, albumArtist, showAlbumToast]);

  const handleShowMenu = useCallback((e, track, idx) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuState({
      track,
      index: idx,
      position: {
        x: Math.min(rect.right, window.innerWidth - 230),
        y: rect.top,
      },
    });
  }, []);

  const handleCreatePlaylistAndAdd = useCallback(async (name, track) => {
    const pl = await createPl(name);
    if (!pl) throw new Error('Failed to create playlist');
    await addToPl(pl.id, { ...track, coverUrl });
  }, [createPl, addToPl, coverUrl]);

  const handleAddAlbumTracksToPlaylist = useCallback(async (plId) => {
    if (!displayTracks.length || !plId) return;
    for (const t of displayTracks) {
      try { await addToPl(plId, { ...t, coverUrl }); } catch { /* skip duplicate or network blip */ }
    }
    console.log('[AlbumDetail] album menu add-to-playlist: added', displayTracks.length, 'tracks to playlist', plId);
    showAlbumToast(`${displayTracks.length} tracks added to playlist`);
    setAlbumMenuOpen(false);
    setAlbumMenuPlaylistView(false);
  }, [displayTracks, addToPl, coverUrl, showAlbumToast]);

  const handleCreatePlaylistAndAddAlbum = useCallback(async (name) => {
    const pl = await createPl(name);
    if (!pl) throw new Error('Failed to create playlist');
    await handleAddAlbumTracksToPlaylist(pl.id);
  }, [createPl, handleAddAlbumTracksToPlaylist]);

  if (!album) return null;

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary, #121212)', color: 'var(--text-primary, #fff)' }}>
      {/* Header Section */}
      <div className="flex gap-8 p-6 md:p-8 pb-4 flex-col md:flex-row">
        <div className="flex-shrink-0 self-center md:self-start">
          {isLocalLibraryAlbum && !coverUrl && !localResolutionComplete ? (
            <div
              className="animate-pulse rounded-lg"
              style={{ width: 360, height: 360, background: 'rgba(255,255,255,0.06)' }}
              aria-label="Loading cover art"
            />
          ) : (
            <FlippableAlbumCover 
              frontUrl={coverUrl}
              backUrl={backCoverUrl}
              title={albumTitle}
              size={360}
              onOpenFullView={() => setAlbumOverlay(album)}
              onProxyRecover={isLocalLibraryAlbum ? handleHeroCoverProxyRecover : null}
              wallCoverUrl={wallCoverFallback && wallCoverFallback !== coverUrl ? wallCoverFallback : null}
              fallbackUrl={
                enrichedOnlineCover && enrichedOnlineCover !== coverUrl
                  ? enrichedOnlineCover
                  : (coverUrl && isCoverThumbRequest(coverUrl)
                    ? withoutCoverThumbParam(coverUrl)
                    : null)
              }
            />
          )}
        </div>
        
        <div className="flex-1 flex flex-col justify-center min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
            {albumType || 'ALBUM'}
          </p>
          
          <h1
            className="text-3xl md:text-4xl font-bold mb-1 leading-tight cursor-pointer transition-colors"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif', color: 'var(--text-primary, #fff)' }}
            onClick={() => setAlbumOverlay(album)}
            onMouseOver={(e) => { e.currentTarget.style.color = GOLD; }}
            onMouseOut={(e) => { e.currentTarget.style.color = ''; }}
          >
            {albumTitle}
          </h1>
          
          <button
            onClick={() => onArtistClick && onArtistClick(album.artist_id || album.artistId, albumArtist)}
            className="hover:underline text-lg mb-1 text-left transition"
            style={{ color: GOLD }}
          >
            {albumArtist}
          </button>

          {/* Artist bio — enriched local library or cloud catalog */}
          {(artistBioTeaser || artistBioLoading) && (
            <div className="mb-2" style={{ overflow: 'hidden', transition: 'max-height 0.3s ease' }}>
              {artistBioLoading && !artistBioTeaser ? (
                <div className="text-xs animate-pulse" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  {isLocalLibraryAlbum ? 'Loading biography…' : 'Loading artist bio…'}
                </div>
              ) : artistBioTeaser && (
                <>
                  {artistBioExpanded && artistBio ? (
                    <div
                      onClick={() => setArtistBioExpanded(false)}
                      style={{ cursor: 'pointer', whiteSpace: isLocalLibraryAlbum ? 'pre-wrap' : undefined }}
                      title="Click to collapse"
                    >
                      {isLocalLibraryAlbum ? (
                        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary, rgba(255,255,255,0.72))' }}>{artistBio}</p>
                      ) : (
                        <FormattedContent text={artistBio} entityName={albumArtist} onLinkClick={onBioLinkClick || ((name) => onArtistClick && onArtistClick(null, name))} />
                      )}
                    </div>
                  ) : (
                    <div
                      className="line-clamp-2"
                      onClick={() => setArtistBioExpanded(true)}
                      style={{ fontSize: 14, lineHeight: '1.5', color: '#666666', cursor: 'pointer' }}
                      title="Click to expand"
                    >
                      {artistBioTeaser}
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setArtistBioExpanded(!artistBioExpanded); }}
                    className="font-medium transition"
                    style={{ color: GOLD, fontSize: 13, minHeight: 44, display: 'flex', alignItems: 'center' }}
                  >
                    {artistBioExpanded ? 'Show less' : 'Show more'}
                  </button>
                </>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1 text-sm mb-3" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
            {formattedReleaseDate && <span>{formattedReleaseDate}</span>}
            {formattedReleaseDate && trackCount > 0 && <span>·</span>}
            {trackCount > 0 && <span>{trackCount} {trackCount === 1 ? 'track' : 'tracks'}</span>}
            {totalDurationMs > 0 && <><span>·</span><span>{formatTotalDuration(totalDurationMs)}</span></>}
            {albumGenre && <><span>·</span><span>{albumGenre}</span></>}
          </div>

          {(maxBd && albumTier) ? (
            <div className="flex items-center gap-2 mb-2">
              <HiResAudioBadge bitDepth={maxBd} sampleRate={maxSr} height={20} />
              {!qualifiesHiResAudioBadge(maxBd, maxSr) && (
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded" style={{ background: albumTier.bg, color: albumTier.color, border: albumTier.border }}>
                  {albumTier.label}
                </span>
              )}
              <span className="text-xs" style={{ color: albumTier.color }}>{maxBd}-bit / {maxSr || 44.1}kHz</span>
            </div>
          ) : isQobuzSource && !maxBd ? (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded" style={{ background: '#2a2a2a', color: '#ccc', border: '1px solid #444' }}>
                CD Quality
              </span>
              <span className="text-xs" style={{ color: '#999' }}>16-bit / 44.1kHz</span>
            </div>
          ) : isLocalLibraryAlbum && albumFormat ? (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded" style={{ background: '#2a2a2a', color: '#ccc', border: '1px solid #444' }}>
                {String(albumFormat).toUpperCase()}
              </span>
              {maxBd && maxSr ? (
                <span className="text-xs" style={{ color: '#999' }}>{maxBd}-bit / {maxSr}kHz</span>
              ) : null}
            </div>
          ) : null}

          {/* Format · Label line */}
          {(albumFormat || albumLabel) && (
            <div className="flex flex-wrap items-center gap-1 text-xs mb-3" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
              {albumFormat && <span>{albumFormat}</span>}
              {albumFormat && albumLabel && <span>·</span>}
              {albumLabel && <span>{albumLabel}</span>}
            </div>
          )}
          
          <div className="flex items-center gap-3 flex-wrap">
            {displayTracks.length > 0 && !streamingCatalogUnavailable && (<>
            <button 
              onClick={() => onTrackPlay && onTrackPlay({ ...albumForPlay, tracks: displayTracks }, 0)}
              className="px-6 py-2.5 rounded font-semibold flex items-center gap-2 transition shadow-lg text-sm"
              style={{ background: GOLD, color: '#1a1a2e' }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              Play now
            </button>
            <button 
              onClick={() => {
                if (onPlayNext && displayTracks.length) {
                  onPlayNext(displayTracks, albumForPlay);
                  showAlbumToast('Album will play next');
                }
              }}
              className="px-4 py-2.5 rounded font-semibold flex items-center gap-2 transition text-sm"
              style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.08))', color: 'var(--text-primary, #fff)', border: '1px solid var(--border, rgba(255,255,255,0.15))' }}
              title="Play next"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>
              Play next
            </button>
            <button
              onClick={() => {
                if (onAddToQueue && displayTracks.length) {
                  onAddToQueue(displayTracks, albumForPlay);
                  showAlbumToast(`${displayTracks.length} tracks added to queue`);
                } else {
                  handleAddAllToQueue();
                }
              }}
              className="px-4 py-2.5 rounded font-semibold flex items-center gap-2 transition text-sm"
              style={{ background: 'var(--bg-secondary, rgba(255,255,255,0.08))', color: 'var(--text-primary, #fff)', border: '1px solid var(--border, rgba(255,255,255,0.15))' }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/></svg>
              Add to Queue
            </button>
            </>)}
            <button
              onClick={() => toggleFav('album', String(album.id || album.serviceAlbumId || ''), album.source || 'maestro', { title: albumTitle, artist: albumArtist, coverUrl })}
              className="w-10 h-10 rounded-full border transition flex items-center justify-center"
              style={{ borderColor: isAlbumFav ? GOLD : 'var(--border, rgba(255,255,255,0.2))', color: isAlbumFav ? GOLD : 'var(--text-secondary, rgba(255,255,255,0.6))' }}
              title={isAlbumFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg className="w-5 h-5" fill={isAlbumFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
            <div className="relative" ref={albumMenuRef}>
              <button
                onClick={() => setAlbumMenuOpen(!albumMenuOpen)}
                className="w-10 h-10 rounded-full border transition flex items-center justify-center"
                style={{ borderColor: albumMenuOpen ? GOLD : 'var(--border, rgba(255,255,255,0.2))', color: albumMenuOpen ? GOLD : 'var(--text-secondary, rgba(255,255,255,0.6))' }}
                title="More options"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>
              </button>
              {albumMenuOpen && (
                <div
                  className="absolute right-0 top-12 z-50 rounded-xl overflow-hidden shadow-2xl"
                  style={{
                    background: 'rgba(255,255,255,0.97)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    minWidth: 200,
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  {albumMenuPlaylistView ? (
                    <AlbumMenuPlaylistPanel
                      playlists={playlists}
                      newName={albumMenuNewPlaylistName}
                      onNewNameChange={setAlbumMenuNewPlaylistName}
                      onCreate={handleCreatePlaylistAndAddAlbum}
                      onAddToPlaylist={handleAddAlbumTracksToPlaylist}
                      onBack={() => setAlbumMenuPlaylistView(false)}
                    />
                  ) : (
                    <>
                      <button
                        onClick={handleCopyAlbumLink}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition"
                        style={{ color: '#1a1a2e' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#666' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        Copy link
                      </button>
                      <button
                        onClick={() => {
                          handleAddAllToQueue();
                          setAlbumMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition"
                        style={{ color: '#1a1a2e' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#666' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                        Add all to queue
                      </button>
                      <button
                        onClick={() => setAlbumMenuPlaylistView(true)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition"
                        style={{ color: '#1a1a2e' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#666' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" /></svg>
                        Add to Playlist
                      </button>
                      <button
                        onClick={() => {
                          toggleFav('album', String(album.id || album.serviceAlbumId || ''), album.source || 'maestro', { title: albumTitle, artist: albumArtist, coverUrl });
                          setAlbumMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition"
                        style={{ color: '#1a1a2e' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: isAlbumFav ? GOLD : '#666' }} fill={isAlbumFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                        {isAlbumFav ? 'Remove from favorites' : 'Add to favorites'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Genre Pills — horizontal scrollable row (only validated genres with albums) */}
      {validatedGenres.length > 0 && (
        <div
          className="px-6 pb-4"
          style={{ overflow: 'hidden' }}
        >
          <div
            className="genre-pills-row"
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              flexWrap: 'nowrap',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {validatedGenres.map((genre, i) => {
              const genreObj = typeof genre === 'string' ? { id: null, name: genre } : genre;
              const genreName = genreObj.name || genreObj.tag || '';
              if (!genreName) return null;
              return (
                <button 
                  key={i}
                  onClick={() => {
                    setGenreSlideout({ open: true, genreId: genreObj.id || null, genreName });
                  }}
                  className="genre-pill"
                  style={{
                    flexShrink: 0,
                    padding: '6px 14px',
                    borderRadius: 999,
                    background: '#2a2a2a',
                    border: '1px solid #3a3a3a',
                    color: '#cccccc',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#3a3a3a'; e.currentTarget.style.color = '#ffffff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.color = '#cccccc'; }}
                >
                  {genreName}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Bio + Metadata Panel */}
      <div className="px-6 md:px-8 pb-6 flex gap-6 flex-col md:flex-row">
        <div className="flex-1 min-w-0">
          {(bioToRender || recordingHistory || bandEraContext || !isTemplateBio) ? (
            <div 
              className={`leading-relaxed cursor-pointer transition-all duration-300 ${!bioExpanded ? 'relative' : ''}`}
              style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}
              onClick={() => setBioExpanded(!bioExpanded)}
            >
              {cleanedBio ? (
                <div className={!bioExpanded && !isTemplateBio ? 'line-clamp-3' : ''}>
                  {isTemplateBio ? (
                    bioExpanded ? (
                      renderNarrativeText(bioToRender, onPersonClick, album?.linkMap || null, {
                        title: albumTitle,
                        artistName: albumArtist,
                        onArtistClick: () => onArtistClick && onArtistClick(album.artist_id || album.artistId, albumArtist)
                      })
                    ) : (
                      <p
                        className="text-white/70 mb-3 leading-relaxed line-clamp-3"
                        style={{ fontSize: '14px' }}
                      >
                        {templateCollapsedTeaser}
                      </p>
                    )
                  ) : (
                    <FormattedContent text={bioToRender} entityName={albumTitle} onLinkClick={onBioLinkClick || ((name) => onArtistClick && onArtistClick(null, name))} linkNames={albumArtist ? [albumArtist] : null} />
                  )}
                </div>
              ) : null}
              
              {!bioExpanded && cleanedBio && !isTemplateBio && cleanedBio.length > 200 && (
                <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none" style={{ background: 'linear-gradient(to top, var(--bg-primary, #121212), transparent)' }} />
              )}
              {!bioExpanded && isTemplateBio && templateBioTeaserLongEnough(templateCollapsedTeaser) && (
                <div className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none" style={{ background: 'linear-gradient(to top, var(--bg-primary, #121212), transparent)' }} />
              )}
              
              {(cleanedBio && (
                isTemplateBio
                  ? templateBioTeaserLongEnough(templateCollapsedTeaser)
                  : (cleanedBio.length > 200 || bioWasTruncated)
              )) && (
                <button 
                  className="text-sm mt-2 flex items-center gap-1 transition font-medium"
                  style={{ color: GOLD, minHeight: 44, display: 'flex', alignItems: 'center' }}
                  onClick={(e) => { e.stopPropagation(); setBioExpanded(!bioExpanded); }}
                >
                  {bioExpanded ? (
                    <>Show less <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7"/></svg></>
                  ) : (
                    <>Show more <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg></>
                  )}
                </button>
              )}

              {!isTemplateBio && enrichSpinnerVisible && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border, rgba(255,255,255,0.2))', borderTopColor: GOLD }} />
                    Generating detailed album description…
                  </div>
                </div>
              )}
              
              {bioExpanded && recordingHistory && (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Recording</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>{recordingHistory}</p>
                </div>
              )}
            </div>
          ) : enrichSpinnerVisible && (apiAlbumLoading || album.enriching || album.needsEnrichment) ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
              <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--border, rgba(255,255,255,0.2))', borderTopColor: GOLD }} />
              Loading album description...
            </div>
          ) : null}
        </div>
        
        {/* Metadata Panel */}
        <div className="w-56 flex-shrink-0 space-y-1" style={{ borderLeft: '1px solid var(--border, rgba(255,255,255,0.1))', paddingLeft: 16 }}>
          {(maxBd && albumTier) ? (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Quality</span>
              <span className="text-xs font-semibold" style={{ color: albumTier.tier !== 'cd' ? albumTier.color : 'var(--text-primary, #fff)' }}>
                {albumTier.label} · {maxBd}-bit / {maxSr || 44.1}kHz
              </span>
            </div>
          ) : isQobuzSource ? (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Quality</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary, #fff)' }}>
                CD Quality · 16-bit / 44.1kHz
              </span>
            </div>
          ) : isLocalLibraryAlbum && albumFormat ? (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Format</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary, #fff)' }}>
                {String(albumFormat).toUpperCase()}{maxBd ? ` · ${maxBd}-bit / ${maxSr || 44.1}kHz` : ''}
              </span>
            </div>
          ) : null}
          {totalDurationMs > 0 && (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Length</span>
              <span className="text-xs" style={{ color: 'var(--text-primary, #fff)' }}>{formatTotalDuration(totalDurationMs)}</span>
            </div>
          )}
          {albumFormat && (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Format</span>
              <span className="text-xs" style={{ color: 'var(--text-primary, #fff)' }}>
                {albumFormat}
              </span>
            </div>
          )}
          {albumLabel && (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Label</span>
              <span className="text-xs truncate ml-2 max-w-[120px]" style={{ color: 'var(--text-primary, #fff)' }}>{albumLabel}</span>
            </div>
          )}
          {album.rating && (
            <div className="flex justify-between items-center py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Rating</span>
              <StarRating rating={album.rating} />
            </div>
          )}
          {studioInfo.name && (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Studio</span>
              <span className="text-xs truncate ml-2 max-w-[120px]" style={{ color: 'var(--text-primary, #fff)' }}>{studioInfo.name}</span>
            </div>
          )}
          {charts.length > 0 && charts[0]?.peak && (
            <div className="flex justify-between py-1">
              <span className="text-xs" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Peak</span>
              <span className="text-xs" style={{ color: 'var(--text-primary, #fff)' }}>#{charts[0].peak}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Optional admin section (e.g. local-only Manage) */}
      {adminSection}

      {/* Tabs */}
      <div className="px-6" style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
        <div className="flex gap-8">
          {['TRACKS', 'CREDITS'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab.toLowerCase())}
              className="py-3 text-sm font-medium tracking-wider transition"
              style={{
                borderBottom: activeTab === tab.toLowerCase() ? `2px solid ${GOLD}` : '2px solid transparent',
                color: activeTab === tab.toLowerCase() ? 'var(--text-primary, #fff)' : 'var(--text-secondary, rgba(255,255,255,0.5))',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="px-6 py-4">
        {mirrorLoading && (
          <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
            <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            Loading album data...
          </div>
        )}
        
        {/* Tracks Tab */}
        {activeTab === 'tracks' && (
          <div>
            {/* Controls */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <button className="text-white/70 hover:text-white text-sm flex items-center gap-1">
                  <span className="text-white/50">&gt;</span> Focus
                </button>
              </div>
              <button className="text-white/50 hover:text-white text-sm flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter
              </button>
            </div>
            
            {/* Track List */}
            {streamingCatalogUnavailable ? (
              <div className="py-10 px-4 text-center space-y-4">
                <p className="text-[15px] font-semibold" style={{ color: 'var(--text-primary, #fff)' }}>
                  Not available to stream on Qobuz in your region
                </p>
                <p className="text-[13px] max-w-md mx-auto" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.55))' }}>
                  This album is in your streaming library, but none of its tracks can be played from Qobuz where you are signed in.
                </p>
                {localLibraryMatch && (
                  <button
                    type="button"
                    onClick={() => {
                      const key = String(localLibraryMatch.album_key || localLibraryMatch.id || '').trim();
                      if (!key) return;
                      navigate(buildLocalLibraryAlbumUrl(encodeLocalAlbumKey(key)));
                    }}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: GOLD, color: '#1a1a2e' }}
                  >
                    Play from your library
                  </button>
                )}
              </div>
            ) : displayTracks.length > 0 ? (
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
                {displayTracks.map((track, index) => {
                  const trackId = String(track.serviceTrackId || track.id || index);
                  const isThisTrackActive = currentTrackId != null && (
                    String(track.id) === String(currentTrackId) ||
                    (track.serviceTrackId && String(track.serviceTrackId) === String(currentTrackId))
                  );
                  return (
                    <TrackRow
                      key={track.id || index}
                      track={track}
                      index={index}
                      isPlaying={isPlaying && isThisTrackActive}
                      isCurrentTrack={isThisTrackActive}
                      isSelected={selectedTrackIndex === index}
                      expanded={expandedTrackIndex === index}
                      onToggleExpand={() => handleToggleTrackExpand(index, track.recordingMbid)}
                      onSelect={onTrackSelect || (() => {})}
                      onPlay={(idx) => { onTrackPlay && onTrackPlay({ ...albumForPlay, tracks: displayTracks }, idx, track); }}
                      onPersonClick={onPersonClick}
                      isFav={checkFav('track', trackId)}
                      onToggleFavorite={() => toggleFav('track', trackId, track.source || album.source || 'maestro', { title: track.title, artistName: track.artistName || albumArtist, duration: track.duration_ms, coverUrl, albumId: String(album.serviceAlbumId || album.id || ''), albumTitle: albumTitle, maxBitDepth: track.maximum_bit_depth || track.maxBitDepth || maxBd || null, maxSampleRate: track.maximum_sampling_rate || track.maxSampleRate || maxSr || null })}
                      onShowMenu={handleShowMenu}
                      onAddToQueue={() => handleAddTrackToQueue(track)}
                      recordingCredits={track.recordingMbid ? recordingCreditsByMbid[track.recordingMbid] : null}
                      recordingCreditsLoading={track.recordingMbid ? !!recordingCreditsLoading[track.recordingMbid] : false}
                      albumSource={album.source}
                    />
                  );
                })}
              </div>
            ) : (
              <AlbumTracksEmptyState
                mirrorLoading={mirrorLoading} isLocal={isLocalLibraryAlbum} qobuzAlbumId={qobuzAlbumId} qobuzCatalogLoaded={qobuzCatalogLoaded}
                streamingCatalogUnavailable={streamingCatalogUnavailable} onRetry={() => setQobuzRetryNonce((n) => n + 1)}
              />
            )}
          </div>
        )}
        
        {/* Credits Tab — powered by useAlbumCredits hook */}
        {activeTab === 'credits' && (
          <div className="space-y-6">
            {creditsLoading && !hookCredits && !albumCreditsByRole && (
              <div className="flex items-center gap-2 text-white/50 text-sm py-4">
                <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                Loading credits...
              </div>
            )}
            {(hookCredits || albumCreditsByRole) ? (
              Object.entries(hookCredits || albumCreditsByRole).map(([role, entries]) => (
                <div key={role}>
                  <h4 className="text-sm uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>
                    {role.replace(/_/g, ' ')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(entries) ? entries : []).map((e, i) => {
                      const name = e?.artist_name || e?.name;
                      const mbid = e?.artist_mbid || e?.mbid || null;
                      const isContributorRole = /producer|engineer|mixing|mastering|studio|arranger|orchestrat/i.test(role);
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            if (!name) return;
                            if (mbid && isContributorRole && onPersonClick) {
                              onPersonClick(mbid, name, { strict: true, mbid });
                            } else if (onArtistClick) {
                              onArtistClick(mbid, name);
                            }
                          }}
                          className="hover:underline text-sm" style={{ color: GOLD }}
                        >
                          {name || 'Unknown'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : credits && Object.keys(credits).length > 0 ? (
              <>
                {credits.producers?.length > 0 && (
                  <div>
                    <h4 className="text-sm uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>Producers</h4>
                    <div className="flex flex-wrap gap-2">
                      {credits.producers.map((p, i) => {
                        const name = typeof p === 'string' ? p : p.name;
                        return (
                          <button key={i} onClick={() => onArtistClick && onArtistClick(null, name)} className="hover:underline text-sm" style={{ color: GOLD }}>
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : !creditsLoading ? (
              <p className="py-8 text-center" style={{ color: 'var(--text-secondary, rgba(255,255,255,0.5))' }}>No credits available for this album.</p>
            ) : null}
          </div>
        )}
      </div>

      {/* Discovery rows: discography, similar albums, similar artists, credits */}
      {!isLocalLibraryAlbum && (
        <AlbumDiscoveryRows
          albumId={album.serviceAlbumId || album.id}
          artistName={albumArtist}
          onAlbumClick={onAlbumClick}
          onArtistClick={onArtistClick}
          onPersonClick={onPersonClick}
        />
      )}

      {/* Track context menu */}
      {menuState && (
        <TrackContextMenu
          track={menuState.track}
          album={album}
          position={menuState.position}
          onClose={() => setMenuState(null)}
          onPlayNow={(t) => {
            const idx = displayTracks.findIndex(dt => (dt.id || dt.serviceTrackId) === (t.id || t.serviceTrackId));
            if (onTrackPlay) onTrackPlay({ ...albumForPlay, tracks: displayTracks }, idx >= 0 ? idx : 0, t);
          }}
          onPlayNext={(t) => {
            const item = { track: { id: t.id || t.serviceTrackId, title: t.title || 'Unknown', artist: t.artistName || albumArtist, album: albumTitle, artworkUrl: coverUrl, duration: t.duration_ms ? Math.floor(t.duration_ms / 1000) : (t.duration || 0), streamUrl: '', source: t.source || album.source || 'maestro', qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || t.id } };
            useMaestroStore.getState().playNext([item]);
          }}
          onPlayOnlyThisTrack={(t) => {
            onPlayOnlyThisTrack?.(t, album, displayTracks);
          }}
          onAddToQueue={(t) => handleAddTrackToQueue(t)}
          playlists={playlists}
          onAddToPlaylist={(plId, t) => addToPl(plId, { ...t, coverUrl })}
          onCreatePlaylist={handleCreatePlaylistAndAdd}
          onGoToArtist={(name) => onArtistClick && onArtistClick(null, name)}
          onGoToAlbum={() => { if (onAlbumClick && album) onAlbumClick(album); }}
          isFav={menuState.track ? checkFav('track', String(menuState.track.id || menuState.track.serviceTrackId || menuState.index)) : false}
          onToggleFavorite={() => {
            const t = menuState.track;
            const trackId = String(t?.id || t?.serviceTrackId || menuState.index);
            toggleFav('track', trackId, t?.source || album?.source || 'maestro', {
              title: t?.title, artistName: t?.artistName || albumArtist, duration: t?.duration_ms, coverUrl,
              albumId: String(album?.serviceAlbumId || album?.id || ''), albumTitle,
            });
          }}
        />
      )}

      {/* Genre browse slideout */}
      <GenreBrowseSlideout
        genreId={genreSlideout.genreId}
        genreName={genreSlideout.genreName}
        source={album.source || 'qobuz'}
        isOpen={genreSlideout.open}
        onClose={() => setGenreSlideout({ open: false, genreId: null, genreName: '' })}
        onOpenAlbum={(a) => onAlbumClick?.(a)}
      />
      
      {albumToast && (
        <div style={{
          position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-card, #1e1e30)', color: 'var(--text-primary)', padding: '10px 20px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          border: '1px solid var(--border)',
          animation: 'popIn 0.12s ease',
        }}>
          {albumToast}
        </div>
      )}

      <div style={{ height: '120px' }} />
    </div>
  );
};

export default AlbumDetailView;
